import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import DataTable from "react-data-table-component";
import Select from "react-select";
import * as yup from "yup";
import jsPDF from "jspdf";

/* COMPONENTS */
import Input from "../../components/input";

/* MATERIAL TAILWIND COMPONENTS */
import {
  Button,
  Dialog,
  DialogBody,
  Card,
  CardBody,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
} from "@material-tailwind/react";

/* ICONS */
import { IoClose } from "react-icons/io5";
import { FaDownload } from "react-icons/fa";
import { GoKebabHorizontal } from "react-icons/go";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* API IMPORT */
import {
  getAwardApplications,
  deleteAwardApplicationById,
} from "../../service/api/contactUs.api";

/* CONFIG */
import { IMAGE_URL } from "../../config";
import ExcelExport from "components/export";
import { useFormik } from "formik";
import {
  sendEmailToSelected,
  updateApplication,
  updateUserStatusJury,
  updateUserStatusOffice,
} from "service/api/auth.api";
import moment from "moment";
import "../../assets/IBMPlexSans-bold";
import "../../assets/IBMPlexSans-normal";

const exportPDF = async (application) => {
  const getBase64FromUrl = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const lineHeight = 8;
  const sectionPadding = 5;
  const sectionTopPadding = 5;
  let y = margin;

  const maxWidth = pageWidth - 2 * margin;

  // ✅ Page break checker
  const checkPageBreak = (requiredHeight = lineHeight) => {
    if (y + requiredHeight > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // === HEADER IMAGE ===
  const logoBase64 = await getBase64FromUrl(
    "https://storage.googleapis.com/cdn-roundtechsquare/lohana-mahaparishad/form-header.png"
  );
  const tempImg = new Image();
  tempImg.src = logoBase64;

  await new Promise((resolve) => {
    tempImg.onload = resolve;
  });

  const imgRatio = tempImg.height / tempImg.width;
  const imgWidth = pageWidth;
  const imgHeight = imgWidth * imgRatio;

  const labelX = margin + sectionPadding;
  const valueX = margin + 70;

  checkPageBreak(imgHeight);
  doc.addImage(logoBase64, "PNG", 0, y, imgWidth, imgHeight);
  y += imgHeight;

  const addLine = (label, value) => {
    checkPageBreak(lineHeight * 2);

    // Wrap long values
    const wrappedValue = doc.splitTextToSize(
      value || "N/A",
      maxWidth - (valueX - margin)
    );
    const requiredHeight = wrappedValue.length * lineHeight;

    // Label
    doc.setFont("IBMPlexSans", "bold");
    doc.text(`${label}:`, labelX, y);

    // Value
    doc.setFont("IBMPlexSans", "normal");
    doc.text(wrappedValue, valueX, y);

    // Move Y correctly (prevent overlap)
    y += requiredHeight + 4; // extra padding
  };

  const addWrappedLink = (label, url, displayText = "View Document") => {
    if (!url) return;
    checkPageBreak(lineHeight * 2);
    doc.setFont("IBMPlexSans", "bold");
    doc.text(`${label}:`, labelX, y);
    doc.setFont("IBMPlexSans", "normal");
    const linkWidth = doc.getTextWidth(displayText);
    doc.setTextColor(0, 0, 255);
    doc.text(displayText, valueX, y);
    doc.link(valueX, y - lineHeight + 2, linkWidth, lineHeight, { url });
    doc.setTextColor(0, 0, 0);
    y += lineHeight + 4;
  };

  const getFileUrl = (path) => (path ? `${IMAGE_URL}${path}` : null);

  const fullName = `${application.firstName || ""} ${
    application.middleName || ""
  } ${application.lastName || ""}`.trim();

  const addSection = (title, drawContentCallback) => {
    checkPageBreak(lineHeight + 12);
    doc.setFontSize(13);
    doc.setFont("IBMPlexSans", "bold");
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, y, maxWidth, lineHeight + 6, "F");
    doc.text(title, labelX, y + lineHeight);
    y += lineHeight + 14;
    doc.setFontSize(11);
    doc.setFont("IBMPlexSans", "bold");
    drawContentCallback();
    y += 6;
  };

  /* PERSONAL INFO */
  addSection("Personal Details", () => {
    addLine("Full Name", fullName);
    addLine("Email", application.email);
    addLine("Mobile", application.mobileNumber);
    addLine(
      "Date of Birth",
      moment(application.dateOfBirth).format("DD-MM-YYYY")
    );
    addLine(
      "Application Date",
      moment(application.dateOfApplication).format("DD-MM-YYYY")
    );
    addLine("Lohana Connect ID", application.lohanaConnectId);
    addLine("Address", application.address);
    addLine("State", application.state);
    addLine("District", application.district);
    addLine("Zone", application.zone);
    addLine("Mahajan", application.mahajan);
    addLine(
      "Award Category",
      Array.isArray(application.awardCategory)
        ? application.awardCategory.join(", ")
        : application.awardCategory
    );
    addWrappedLink("Photograph", getFileUrl(application.photograph));
    addWrappedLink("Signature", getFileUrl(application.applicantSignature));
  });

  if (application.class) {
    addSection("Education Details", () => {
      addLine("Board", application.class.boardName);
      addLine(
        "Marks",
        `${application.class.obtainedMarks}/${application.class.totalMarks}`
      );
      addLine("Achievements", application.class.achievements);
      addWrappedLink("Marksheet", getFileUrl(application.class.marksheet));
    });
  }

  if (application.graduation) {
    addSection("Education Details", () => {
      addLine("Program", application.graduation.program);
      addLine("Course", application.graduation.course);
      addLine("Specialization", application.graduation.specialization);
      addLine("University", application.graduation.university);
      addLine(
        "Marks",
        `${application.graduation.marks}/${application.graduation.graduationTotalMarks}`
      );
      addLine("Achievements", application.graduation.achievements);
      addWrappedLink("Marksheet", getFileUrl(application.graduation.marksheet));
    });
  }

  if (application.postGraduation) {
    addSection("Education Details", () => {
      addLine("Program", application.postGraduation.program);
      addLine("Course", application.postGraduation.course);
      addLine("Specialization", application.postGraduation.specialization);
      addLine("University", application.postGraduation.university);
      addLine(
        "Marks",
        `${application.postGraduation.marks}/${application.postGraduation.graduationTotalMarks}`
      );
      addLine("Achievements", application.postGraduation.achievements);
      addWrappedLink(
        "Marksheet",
        getFileUrl(application.postGraduation.marksheet)
      );
    });
  }

  if (application.phd) {
    addSection("Education Details", () => {
      addLine("Department", application.phd.department);
      addLine("University", application.phd.university);
      addLine("Thesis Title", application.phd.thesisTitle);
      addLine("Achievements", application.phd.achievements);
      addWrappedLink(
        "Degree Certificate",
        getFileUrl(application.phd.degreeCertificate)
      );
      addWrappedLink(
        "University Gazette",
        getFileUrl(application.phd.universityGazette)
      );
    });
  }

  if (application.civilServices) {
    addSection("Civil Services", () => {
      addLine("Commission", application.civilServices.commission);
      addLine("Institute", application.civilServices.institute);
      addLine("Rank", application.civilServices.rank);
      addLine(
        "Passed Month/Year",
        `${application.civilServices.passedMonth}/${application.civilServices.passedYear}`
      );
      addLine(
        "Marks",
        `${application.civilServices.marks}/${application.civilServices.totalMarks}`
      );
      addWrappedLink("Result", getFileUrl(application.civilServices.result));
      addWrappedLink(
        "Letter of Posting",
        getFileUrl(application.civilServices.letterOfPosting)
      );
    });
  }

  if (application.caCaIcwa) {
    addSection(`Professional Details`, () => {
      addLine("Institute", application.caCaIcwa.institute);
      addLine(
        "Foundation Exam",
        `${application.caCaIcwa.foundationExamMarks}/${application.caCaIcwa.foundationExamTotalMarks} (${application.caCaIcwa.foundationExamPassedMonth}/${application.caCaIcwa.foundationExamPassedYear})`
      );
      addWrappedLink(
        "Foundation Marksheet",
        getFileUrl(application.caCaIcwa.foundationMarksheet)
      );
      addLine(
        "Intermediate Exam",
        `${application.caCaIcwa.intermediateExamMarks}/${application.caCaIcwa.intermediateExamTotalMarks} (${application.caCaIcwa.intermediateExamPassedMonth}/${application.caCaIcwa.intermediateExamPassedYear})`
      );
      addWrappedLink(
        "Intermediate Marksheet",
        getFileUrl(application.caCaIcwa.intermediateMarksheet)
      );
      addLine(
        "Final Exam",
        `${application.caCaIcwa.finalExamMarks}/${application.caCaIcwa.finalExamTotalMarks} (${application.caCaIcwa.finalExamPassedMonth}/${application.caCaIcwa.finalExamPassedYear})`
      );
      addWrappedLink(
        "Final Marksheet",
        getFileUrl(application.caCaIcwa.finalMarksheet)
      );
      addLine("Achievements", application.caCaIcwa.achievements);
    });
  }

  if (application.documents) {
    const docs = application.documents;
    addSection("Supporting Documents", () => {
      addWrappedLink("Passport Photo", getFileUrl(docs.passportSizePhotograph));
      addWrappedLink("Proof of Identity", getFileUrl(docs.proofOfIdentity));
      addWrappedLink(
        "Letter of Recommendation",
        getFileUrl(docs.letterOfRecommendation)
      );
      addWrappedLink(
        "Registration Certificate",
        getFileUrl(docs.copyOfRegistrationCertificate)
      );
      addWrappedLink(
        "Certificates",
        getFileUrl(docs.certifiedTranscriptsAndAchievementCertificates)
      );
      addWrappedLink("Proof of Age", getFileUrl(docs.proofOfAge));
      addWrappedLink("Registration Proof", getFileUrl(docs.registrationProof));
      addWrappedLink(
        "Additional Documents",
        getFileUrl(docs.additionalDocuments)
      );
    });
  }

  doc.save(`${fullName}_Application.pdf`);
};

const zones = [
  "Saurashtra 1",
  "Saurashtra 2",
  "Saurashtra 3",
  "Saurashtra 4",
  "Kutch",
  "North Gujarat",
  "Central Gujarat",
  "South Gujarat",
  "Mumbai",
  "Maharashtra",
  "Central India",
  "Vidarbha",
  "South India",
  "East India",
  "North India",
];

const statesOfIndia = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const options = [
  { value: null, label: "All categories" },
  { value: "Ph.D Achievers", label: "Ph.D Achievers" },
  { value: "Masters Program Toppers", label: "Masters Program Toppers" },
  { value: "Bachelors Program Toppers", label: "Bachelors Program Toppers" },
  { value: "University Gold Medalists", label: "University Gold Medalists" },
  {
    value: "Civil Services Achievers",
    label: "Civil Services Achievers",
  },
  {
    value: "Medical Degree Achievers",
    label: "Medical Degree Achievers",
  },
  {
    value: "Secondary School Scholars (Class 10)",
    label: "Secondary School Scholars (Std 10)",
  },
  {
    value: "Higher Secondary School Scholars (Class 12)",
    label: "Higher Secondary School Scholars (Class 12)",
  },
  {
    value: "Accountancy & Company Secretary Achievers",
    label: "Accountancy & Company Secretary Achievers",
  },
];

const graduationPrograms = [
  { value: "Arts & Humanities", label: "Arts & Humanities" },
  { value: "Science", label: "Science" },
  { value: "Commerce", label: "Commerce" },
  { value: "Management", label: "Management" },
  { value: "Computer Applications & IT", label: "Computer Applications & IT" },
  { value: "Engineering & Technology", label: "Engineering & Technology" },
  { value: "Law", label: "Law" },
  { value: "Education", label: "Education" },
  { value: "Social Work", label: "Social Work" },
  {
    value: "Library & Information Science",
    label: "Library & Information Science",
  },
  { value: "Physical Education", label: "Physical Education" },
  {
    value: "Hotel Management & Hospitality",
    label: "Hotel Management & Hospitality",
  },
  { value: "Fine Arts", label: "Fine Arts" },
  { value: "Media", label: "Media" },
  { value: "Design", label: "Design" },
  { value: "Pharmacy", label: "Pharmacy" },
  { value: "Medical Sciences", label: "Medical Sciences" },
  { value: "Others", label: "Others" },
];

const boardOptions = [
  { value: "cbse", label: "CBSE" },
  { value: "icse", label: "ICSE" },
  { value: "ig", label: "IG" },
  { value: "igcse", label: "IGCSE" },
  { value: "state", label: "State Board" },
  { value: "other", label: "Other" },
];

const medicalGraduationCourse = [
  {
    value: "MBBS (Bachelor of Medicine and Bachelor of Surgery)",
    label: "MBBS (Bachelor of Medicine and Bachelor of Surgery)",
  },
  {
    value: "BDS (Bachelor of Dental Surgery)",
    label: "BDS (Bachelor of Dental Surgery)",
  },
  {
    value: "MD (Doctor of Medicine)",
    label: "MD (Doctor of Medicine)",
  },
  {
    value: "DM (Doctorate of Medicine)",
    label: "DM (Doctorate of Medicine)",
  },
  {
    value: "MDS (Master of Dental Surgery)",
    label: "MDS (Master of Dental Surgery)",
  },
];

const qualifications = [
  { value: "CA", label: "CA" },
  { value: "CS", label: "CS" },
  { value: "ICWA", label: "ICWA" },
];

const officeBearerOptions = [
  { value: "eligible", label: "Eligible" },
  { value: "ineligible", label: "Ineligible" },
  { value: "rejected", label: "Rejected" },
];

const officeBearerOptionsUpdated = [
  { value: "eligible", label: "Eligible" },
  { value: "ineligible", label: "Ineligible" },
  { value: "rejected", label: "Rejected" },
  { value: "shortlisted", label: "Shortlisted" },
];

const juryOptions = [
  { value: "selected", label: "Selected" },
  { value: "not-selected", label: "Not Selected" },
];

const statusOptionsForSuperAdmin = [
  { value: null, label: "All status" },
  { value: "pending", label: "Pending" },
  { value: "eligible", label: "Eligible" },
  { value: "rejected", label: "Rejected" },
  { value: "ineligible", label: "Ineligible" },
  { value: "selected", label: "Selected" },
  { value: "not-selected", label: "Not Selected" },
  { value: "shortlisted", label: "Shortlisted" },
];

const statusOptionsForJury = [
  { value: "eligible", label: "Eligible" },
  { value: "ineligible", label: "Ineligible" },
  { value: "selected", label: "Selected" },
  { value: "not-selected", label: "Not Selected" },
  { value: "shortlisted", label: "Shortlisted" },
];

const statusOptionsForOfficeBearer = [
  { value: null, label: "All status" },
  { value: "pending", label: "Pending" },
  { value: "eligible", label: "Eligible" },
  { value: "ineligible", label: "Ineligible" },
  { value: "rejected", label: "Rejected" },
  { value: "shortlisted", label: "Shortlisted" },
];

const validationSchema = yup.object().shape({
  applicationStatus: yup.object().required("Status is required"),
});

const Student = () => {
  /* STATE VARIABLES */
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);

  /* LIST OF DATA */
  const [dataList, setDataList] = useState([]);

  /* TOTAL DOCS */
  const [totalDocs, setTotalDocs] = useState(0);

  /* SEARCH VALUE */
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  /* EXPOERTED DATA */
  const [exportData, setExportData] = useState([]);

  /* DELETE ID */
  const [deleteId, setDeleteId] = useState(null);

  /* DELETE MODAL */
  const [deleteModal, setDeleteModal] = useState(false);

  /* SELECTED APPLICATION */
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailsModal, setDetailsModal] = useState(false);

  /* SELECTED CATEGORY */
  const [selectedCategory, setSelectedCategory] = useState({
    value: null,
    label: "All categories",
  });

  /* STATE OPTIONS */
  const [stateOptions, setStateOptions] = useState([]);

  /* ZONE OPTIONS */
  const [zoneOptions, setZoneOptions] = useState([]);

  /* SELECTED STATE AND ZONE */
  const [selectedState, setSelectedState] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);

  /* STATUS DIALOG */
  const [statusDialog, setStatusDialog] = useState(false);
  const [juryStatusDialog, setJuryStatusDialog] = useState(false);

  /* USER ROLE */
  const [userRole, setUserRole] = useState("");

  /* STATUS LOADING */
  const [statusLoading, setStatusLoading] = useState(false);

  /* SELECTED ROLE */
  const [selectedRole, setSelectedRole] = useState(null);

  /* SELECTED ID */
  const [selectedId, setSelectedId] = useState();

  /* SELECTED TAB */
  const [selectedTab, setSelectedTab] = useState("Personal Information");

  /* REWARDS DIALOG */
  const [rewardsDialog, setRewardsDialog] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);

  /* DEFAULT OPTION */
  const [defaultSelected, setDefaultSelected] = useState(null);

  /* GRADUATION PROGRAM FILTER */
  const [selectedGraduationProgram, setSelectedGraduationProgram] =
    useState(null);

  /* POST GRADUATION PROGRAM FILTER */
  const [selectedPostGraduationProgram, setSelectedPostGraduationProgram] =
    useState(null);

  /* BOARD FILTER */
  const [selectedBoard, setSelectedBoard] = useState(null);

  /* QUALIFICATION */
  const [selectedQualification, setSelectedQualification] = useState(null);

  /* PROGRAM FOR MEDICAL */
  const [selectedCourseForMedical, setSelectedCourseForMedical] =
    useState(null);

  /* POST GRADUATION PROGRAM FILTER */
  const [selectedCourseForGoldMedalists, setSelectedCourseForGoldMedalists] =
    useState(null);

  /* SEND EMAIL DIALOG */
  const [sendEmailDialog, setSendEmailDialog] = useState(false);

  /* EDIT MODAL DIALOG */
  const [editModal, setEditModal] = useState(false);
  const [editTab, setEditTab] = useState("Personal Information");

  const [isShortlistedValue, setIsShortlistedValue] = useState("");

  /* GET LIST OF DATA */
  const getDataListFunction = () => {
    setLoading(true);
    const params = {
      page: 1,
      sizePerPage: 10000,
      pagination: true,
    };

    if (selectedCategory.value !== null) {
      params.awardCategory = selectedCategory.value;
    }

    if (selectedState !== null) {
      params.state = selectedState?.value;
    }

    if (selectedZone !== null) {
      params.zone = selectedZone?.value;
    }

    if (defaultSelected !== null && defaultSelected?.value !== "shortlisted") {
      params.applicationStatus = defaultSelected?.value;
    } else if (defaultSelected?.value === "shortlisted") {
      params.applicationStatus = "eligible";
      params.isShortListed = true;
    }

    getAwardApplications(params)
      .then((res) => {
        let allData = res?.data?.docs || [];

        /* FILTER DATA OF STUDENTS */
        allData = allData.filter((item) => {
          const hasInstitute =
            item.institute && typeof item.institute === "object";
          const hasTeachingProfessionals =
            item.teachingProfessionals &&
            typeof item.teachingProfessionals === "object";
          return !hasInstitute && !hasTeachingProfessionals;
        });

        /* SEARCH BASED ON FILTERS */
        if (debouncedSearch.trim()) {
          const searchLower = search.toLowerCase();

          allData = allData.filter((item) => {
            const fullName = `${item.firstName || ""} ${
              item.middleName || ""
            } ${item.lastName || ""}`.toLowerCase();
            const email = (item.email || "").toLowerCase();
            const regularMatch =
              fullName.includes(searchLower) || email.includes(searchLower);

            return regularMatch;
          });
        }

        /* SEARCH BASED ON GRADUATION PROGRAM */
        if (selectedGraduationProgram !== null) {
          if (selectedState !== null) {
            allData = allData.filter((item) => {
              return (
                item?.state === selectedState?.value &&
                item?.graduation?.program === selectedGraduationProgram?.value
              );
            });
          } else if (selectedZone !== null) {
            allData = allData.filter((item) => {
              return (
                item?.zone === selectedZone?.value &&
                item?.graduation?.program === selectedGraduationProgram?.value
              );
            });
          } else if (selectedZone !== null && selectedState !== null) {
            allData = allData.filter((item) => {
              return (
                item?.state === selectedState?.value &&
                item?.zone === selectedZone?.value &&
                item?.graduation?.program === selectedGraduationProgram?.value
              );
            });
          } else {
            allData = allData.filter((item) => {
              return (
                item?.graduation?.program === selectedGraduationProgram?.value
              );
            });
          }
        }

        /* SEARCH BASED ON POST - GRADUATION PROGRAM */
        if (selectedPostGraduationProgram !== null) {
          if (selectedState !== null) {
            allData = allData.filter((item) => {
              return (
                item?.state === selectedState?.value &&
                item?.postGraduation?.program ===
                  selectedPostGraduationProgram?.value
              );
            });
          } else if (selectedZone !== null) {
            allData = allData.filter((item) => {
              return (
                item?.zone === selectedZone?.value &&
                item?.postGraduation?.program ===
                  selectedPostGraduationProgram?.value
              );
            });
          } else if (selectedZone !== null && selectedState !== null) {
            allData = allData.filter((item) => {
              return (
                item?.state === selectedState?.value &&
                item?.zone === selectedZone?.value &&
                item?.postGraduation?.program ===
                  selectedPostGraduationProgram?.value
              );
            });
          } else {
            allData = allData.filter((item) => {
              return (
                item?.postGraduation?.program ===
                selectedPostGraduationProgram?.value
              );
            });
          }
        }

        /* SEARCH BASED ON BOARD */
        if (selectedBoard !== null) {
          if (selectedState !== null) {
            allData = allData.filter((item) => {
              return (
                item?.state === selectedState?.value &&
                item?.class?.boardName === selectedBoard?.value
              );
            });
          } else if (selectedZone !== null) {
            allData = allData.filter((item) => {
              return (
                item?.zone === selectedZone?.value &&
                item?.class?.boardName === selectedBoard?.value
              );
            });
          } else if (selectedZone !== null && selectedState !== null) {
            allData = allData.filter((item) => {
              return (
                item?.state === selectedState?.value &&
                item?.zone === selectedZone?.value &&
                item?.class?.boardName === selectedBoard?.value
              );
            });
          } else {
            allData = allData.filter((item) => {
              return item?.class?.boardName === selectedBoard?.value;
            });
          }
        }

        /* SEARCH BASED ON QUALIFICATION */
        if (selectedQualification !== null) {
          if (selectedState !== null) {
            allData = allData.filter((item) => {
              return (
                item?.state === selectedState?.value &&
                item?.caCaIcwa?.qualification === selectedQualification?.value
              );
            });
          } else if (selectedZone !== null) {
            allData = allData.filter((item) => {
              return (
                item?.zone === selectedZone?.value &&
                item?.caCaIcwa?.qualification === selectedQualification?.value
              );
            });
          } else if (selectedZone !== null && selectedState !== null) {
            allData = allData.filter((item) => {
              return (
                item?.state === selectedState?.value &&
                item?.zone === selectedZone?.value &&
                item?.caCaIcwa?.qualification === selectedQualification?.value
              );
            });
          } else {
            allData = allData.filter((item) => {
              return (
                item?.caCaIcwa?.qualification === selectedQualification?.value
              );
            });
          }
        }

        /* SEARCH FOR MEDICAL DEGREE ACHIEVERS */
        if (selectedCourseForMedical !== null) {
          if (selectedState !== null) {
            allData = allData.filter((item) => {
              return (
                item?.state === selectedState?.value &&
                (item?.graduation?.course || item?.postGraduation?.course) ===
                  selectedCourseForMedical?.value
              );
            });
          } else if (selectedZone !== null) {
            allData = allData.filter((item) => {
              return (
                item?.zone === selectedZone?.value &&
                (item?.graduation?.course || item?.postGraduation?.course) ===
                  selectedCourseForMedical?.value
              );
            });
          } else if (selectedZone !== null && selectedState !== null) {
            allData = allData.filter((item) => {
              return (
                item?.state === selectedState?.value &&
                item?.zone === selectedZone?.value &&
                (item?.graduation?.course || item?.postGraduation?.course) ===
                  selectedCourseForMedical?.value
              );
            });
          } else {
            allData = allData.filter((item) => {
              return (
                (item?.graduation?.course || item?.postGraduation?.course) ===
                selectedCourseForMedical?.value
              );
            });
          }
        }

        /* SEARCH FOR UNIVERSITY GOLD MEDALISTS */
        if (selectedCourseForGoldMedalists !== null) {
          if (selectedState !== null) {
            allData = allData.filter((item) => {
              return (
                item?.state === selectedState?.value &&
                (item?.graduation?.program || item?.postGraduation?.program) ===
                  selectedCourseForGoldMedalists?.value
              );
            });
          } else if (selectedZone !== null) {
            allData = allData.filter((item) => {
              return (
                item?.zone === selectedZone?.value &&
                (item?.graduation?.program || item?.postGraduation?.program) ===
                  selectedCourseForGoldMedalists?.value
              );
            });
          } else if (selectedZone !== null && selectedState !== null) {
            allData = allData.filter((item) => {
              return (
                item?.state === selectedState?.value &&
                item?.zone === selectedZone?.value &&
                (item?.graduation?.program || item?.postGraduation?.program) ===
                  selectedCourseForGoldMedalists?.value
              );
            });
          } else {
            allData = allData.filter((item) => {
              return (
                (item?.graduation?.program || item?.postGraduation?.program) ===
                selectedCourseForGoldMedalists?.value
              );
            });
          }
        }

        /* START INDEX */
        const startIndex = (page - 1) * sizePerPage;

        /* END INDEX */
        const endIndex = startIndex + sizePerPage;

        /* PAGINATED DATA */
        const paginatedData = allData.slice(startIndex, endIndex);
        setDataList(paginatedData);
        setTotalDocs(allData.length);
      })
      .catch((e) => {
        console.error("Error in component:", e);
        toast.error("Failed to fetch data");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* EXCEL EXPORT */
  const getExportList = () => {
    return new Promise((resolve, reject) => {
      setLoading(true);
      const params = {
        page: 1,
        sizePerPage: 10000,
        pagination: true,
      };

      if (selectedCategory.value !== null) {
        params.awardCategory = selectedCategory.value;
      }

      if (selectedState !== null) {
        params.state = selectedState?.value;
      }

      if (selectedZone !== null) {
        params.zone = selectedZone?.value;
      }

      if (
        defaultSelected !== null &&
        defaultSelected?.value !== "shortlisted"
      ) {
        params.applicationStatus = defaultSelected?.value;
      } else if (defaultSelected?.value === "shortlisted") {
        params.applicationStatus = "eligible";
        params.isShortListed = true;
      }

      getAwardApplications(params)
        .then((res) => {
          let allData = res?.data?.docs || [];

          /* FILTER ONLY STUDENT DATA */
          allData = allData.filter((item) => {
            const hasInstitute =
              item.institute && typeof item.institute === "object";
            const hasTeachingProfessionals =
              item.teachingProfessionals &&
              typeof item.teachingProfessionals === "object";
            return !hasInstitute && !hasTeachingProfessionals;
          });

          const tempArray = allData?.map((item, index) => {
            const fullName = `${item?.firstName ?? ""} ${
              item?.middleName ?? ""
            } ${item?.lastName ?? ""}`.trim();
            const photographUrl = item?.photograph
              ? `https://storage.googleapis.com/kidsstreet-test/${item.photograph}`
              : "N/A";
            const signatureUrl = item?.applicantSignature
              ? `https://storage.googleapis.com/kidsstreet-test/${item.applicantSignature}`
              : "N/A";

            const graduation = item?.graduation ?? {};
            const postGraduation = item?.postGraduation ?? {};
            const phd = item?.phd ?? {};
            const classDetails = item?.class ?? {};
            const civilServices = item?.civilServices ?? {};
            const caCaIcwa = item?.caCaIcwa ?? {};
            const documents = item?.documents ?? {};
            const affiliation = item?.affiliation ?? {};

            return {
              "Sr No:": index + 1,
              "Applicant Name": fullName || "N/A",
              State: item?.state ?? "N/A",
              District: item?.district ?? "N/A",
              Zone: item?.zone ?? "N/A",
              Mahajan: item?.mahajan ?? "N/A",
              Email: item?.email ?? "N/A",
              "Mobile Number": item?.mobileNumber ?? "N/A",
              "Application Date": item?.dateOfApplication ?? "N/A",
              "Lohana Connect ID": item?.lohanaConnectId ?? "N/A",
              "Date of birth": item?.dateOfBirth ?? "N/A",
              "Award Category": item?.awardCategory[0] ?? "N/A",
              Address: item?.address ?? "N/A",
              Photograph: photographUrl,
              Signature: signatureUrl,

              /* PHD APPLICATION */
              Department: phd?.department ?? "N/A",
              "Thesis Title": phd?.thesisTitle ?? "N/A",
              Achievements:
                phd?.achievements ??
                postGraduation?.achievements ??
                graduation?.achievements ??
                classDetails?.achievements ??
                caCaIcwa?.achievements ??
                "N/A",
              "University Gazette": phd?.universityGazette ?? "N/A",
              "Degree Certificate": phd?.degreeCertificate
                ? `https://storage.googleapis.com/kidsstreet-test/${phd.degreeCertificate}`
                : "N/A",

              /* POST GRADUATION */
              Program:
                postGraduation?.program ??
                graduation?.program ??
                caCaIcwa?.qualification ??
                "N/A",
              Course: postGraduation?.course ?? graduation?.course ?? "N/A",
              Specialization:
                postGraduation?.specialization ??
                graduation?.specialization ??
                "N/A",
              University:
                postGraduation?.university ??
                graduation?.university ??
                affiliation?.university ??
                "N/A",
              Marksheet:
                postGraduation?.marksheet || graduation?.marksheet
                  ? `https://storage.googleapis.com/kidsstreet-test/${
                      postGraduation?.marksheet ?? graduation?.marksheet
                    }`
                  : "N/A",

              /* CIVIL SERVICES */
              Institute: civilServices?.program ?? caCaIcwa?.institute ?? "N/A",
              Commission: civilServices?.commission ?? "N/A",
              Rank: civilServices?.rank ?? "N/A",
              "Passed Month": civilServices?.passedMonth ?? "N/A",
              "Passed Year": civilServices?.passedYear ?? "N/A",
              "Obtained Marks":
                civilServices?.marks ??
                graduation?.graduationTotalMarks ??
                classDetails?.obtainedMarks ??
                caCaIcwa?.finalExamMarks ??
                "N/A",
              "Total Marks":
                civilServices?.totalMarks ??
                graduation?.marks ??
                classDetails?.totalMarks ??
                caCaIcwa?.finalExamTotalMarks ??
                "N/A",
              Result: civilServices?.result
                ? `https://storage.googleapis.com/kidsstreet-test/${civilServices.result}`
                : "N/A",
              "Letter of Posting": civilServices?.letterOfPosting
                ? `https://storage.googleapis.com/kidsstreet-test/${civilServices.letterOfPosting}`
                : "N/A",

              /* CLASS 10/12 */
              "Board Name": classDetails?.boardName ?? "N/A",

              /* CA/ICWA */
              "Foundation Exam Marks": caCaIcwa?.foundationExamMarks ?? "N/A",
              "Foundation Exam Total Marks":
                caCaIcwa?.foundationExamTotalMarks ?? "N/A",
              "Final Exam Passed Month":
                caCaIcwa?.finalExamPassedMonth ?? "N/A",
              "Foundation Exam Passed Year":
                caCaIcwa?.foundationExamPassedYear ?? "N/A",
              "Foundation Marksheet": caCaIcwa?.foundationMarksheet
                ? `https://storage.googleapis.com/kidsstreet-test/${caCaIcwa.foundationMarksheet}`
                : "N/A",

              "Intermediate Exam Marks":
                caCaIcwa?.intermediateExamMarks ?? "N/A",
              "Intermediate Exam Total Marks":
                caCaIcwa?.intermediateExamTotalMarks ?? "N/A",
              "Intermediate Exam Passed Month":
                caCaIcwa?.intermediateExamPassedMonth ?? "N/A",
              "Intermediate Exam Passed Year":
                caCaIcwa?.intermediateExamPassedYear ?? "N/A",
              "Intermediate Marksheet": caCaIcwa?.intermediateMarksheet
                ? `https://storage.googleapis.com/kidsstreet-test/${caCaIcwa.intermediateMarksheet}`
                : "N/A",

              "Final Exam Marks": caCaIcwa?.finalExamMarks ?? "N/A",
              "Final Exam Total Marks": caCaIcwa?.finalExamTotalMarks ?? "N/A",
              "Final Exam Passed Year": caCaIcwa?.finalExamPassedYear ?? "N/A",
              "Final Marksheet": caCaIcwa?.finalMarksheet
                ? `https://storage.googleapis.com/kidsstreet-test/${caCaIcwa.finalMarksheet}`
                : "N/A",

              /* AFFILIATION */
              Institution: affiliation?.institution ?? "N/A",
              Department: affiliation?.department ?? "N/A",

              /* DOCUMENTS */
              "Certified Transcripts and Achievement Certificate":
                documents?.certifiedTranscriptsAndAchievementCertificates
                  ? `https://storage.googleapis.com/kidsstreet-test/${documents.certifiedTranscriptsAndAchievementCertificates}`
                  : "N/A",
              "Proof of Identity": documents?.proofOfIdentity
                ? `https://storage.googleapis.com/kidsstreet-test/${documents.proofOfIdentity}`
                : "N/A",

              /* STATUS */
              "Application Status":
                defaultSelected?.value === "shortlisted"
                  ? "Eligible & Shortlisted"
                  : item?.applicationStatus,

              /* OFFICE BEARER REMARKS */
              "Office Bearer Remarks":
                (item.officeBearerRemarks || [])
                  .map(
                    (r) =>
                      `${r.remark} (by ${r.addedBy?.firstName || ""} ${
                        r.addedBy?.lastName || ""
                      } on ${moment(r.createdAt).format("DD MMM")})`
                  )
                  .join(", ") || "No remarks",

              /* JURY BEARER REMARKS */
              "Jury Remarks":
                (item.juryRemarks || [])
                  .map(
                    (r) =>
                      `${r.remark} (by ${r.addedBy?.firstName || ""} ${
                        r.addedBy?.lastName || ""
                      } on ${moment(r.createdAt).format("DD MMM")})`
                  )
                  .join(", ") || "No remarks",
            };
          });

          setExportData(tempArray);
          resolve(tempArray);
        })
        .catch((e) => {
          console.error("Export data fetch error:", e);
          toast.error("Failed to fetch export data");
          reject(e);
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  /* FUNCTIONS */
  const handlePageChange = async (e) => {
    setPage(e);
    //getDataListFunction();
  };

  /* HANDLE PER PAGE */
  const handlePerPageChange = async (e) => {
    setSizePerPage(e);
    //getDataListFunction();
  };

  /* HANDLE DELETE DATA */
  const handleDelete = () => {
    setLoading(true);
    deleteAwardApplicationById(deleteId)
      .then((res) => {
        toast.success(res?.message || "Application deleted successfully");
        setDataList((prev) => prev.filter((item) => item._id !== deleteId));
      })
      .catch((e) => {
        console.error("Error deleting application:", e);
        toast.error(
          e?.response?.data?.message || "Failed to delete application"
        );
      })
      .finally(() => {
        setDeleteModal(false);
        setDeleteId(null);
        setLoading(false);
      });
  };

  /* HANDLE SEND EMAIL */
  const handleSendEmail = () => {
    setLoading(true);
    sendEmailToSelected(deleteId)
      .then((res) => {
        toast.success(res?.message || "The email has been sent successfully");
      })
      .catch((e) => {
        console.error("Error deleting application:", e);
        toast.error(
          e?.response?.data?.message || "Failed to delete application"
        );
      })
      .finally(() => {
        setSendEmailDialog(false);
        setDeleteId(null);
        setLoading(false);
      });
  };

  /* GET APPLICANT'S FULL NAME */
  const getFullName = (application) => {
    const { firstName, middleName, lastName } = application;
    return `${firstName || ""} ${middleName || ""} ${lastName || ""}`.trim();
  };

  /* VIEW APPLICATION DETAILS */
  const viewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setDetailsModal(true);
  };

  /* FORMAT DATE */
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return "Invalid date";
    }
  };

  /* GET FULL FILE URL */
  const getFullFileUrl = (path) => {
    if (!path) return null;
    return `${IMAGE_URL}${path}`;
  };

  /* UPDATE FORMIK */
  const updateFormik = useFormik({
    initialValues: {
      applicationStatus: "",
      remarks: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setStatusLoading(true);
      let payload = {};

      if (values?.applicationStatus?.value === "shortlisted") {
        payload = {
          isShortListed: true,
          applicationStatus: "eligible",
        };
      } else if (values?.remarks !== "") {
        payload = {
          applicationStatus: values?.applicationStatus?.value,
          remarks: values?.remarks,
        };
      } else if (values?.remarks === "") {
        payload = {
          applicationStatus: values?.applicationStatus?.value,
        };
      } else {
        console.log("Not received payload");
      }

      updateUserStatusOffice(selectedId, payload)
        .then((res) => {
          if (res?.status) {
            toast.success("The status has been updated successfully");
            setStatusDialog(!statusDialog);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          console.log("Error: ", e);
        })
        .finally(() => {
          setStatusLoading(!statusLoading);
          getDataListFunction();
          updateFormik.resetForm();
        });
    },
  });

  /* UPDATE FORMIK JURY */
  const updateFormikJury = useFormik({
    initialValues: {
      applicationStatus: "",
      remarks: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setStatusLoading(true);
      let payload = {};

      if (values?.remarks !== "") {
        payload = {
          applicationStatus: values?.applicationStatus?.value,
          remarks: values?.remarks,
        };
      }

      if (values?.remarks === "") {
        payload = {
          applicationStatus: values?.applicationStatus?.value,
        };
      }

      updateUserStatusJury(selectedId, payload)
        .then((res) => {
          if (res?.status) {
            toast.success("The status has been updated successfully");
            setJuryStatusDialog(!juryStatusDialog);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          console.log("Error: ", e);
        })
        .finally(() => {
          setStatusLoading(!statusLoading);
          getDataListFunction();
          updateFormikJury.resetForm();
        });
    },
  });

  /* HANDLE STATUS DIALOG */
  const handleStatusDialogOpen = (row) => {
    setSelectedId(row?._id);
    const user = JSON.parse(localStorage.getItem("user"));
    setIsShortlistedValue(row?.applicationStatus);

    if (row?.applicationStatus && row?.applicationStatus !== "pending") {
      const currentStatusOption = officeBearerOptions.find(
        (option) => option.value === row.applicationStatus
      );
      updateFormik.setValues({
        applicationStatus: currentStatusOption || "",
        remarks:
          row?.officeBearerRemarks?.filter(
            (item) => item?.addedBy?._id === user?._id
          )?.remark || "",
      });
      setSelectedRole(currentStatusOption || null);
    } else {
      updateFormik.resetForm();
      setSelectedRole(null);
    }

    setStatusDialog(true);
  };

  /* HANDLE STATUS DIALOG */
  const handleStatusDialogOpenJury = (row) => {
    setSelectedId(row?._id);
    const user = JSON.parse(localStorage.getItem("user"));

    if (row?.applicationStatus && row?.applicationStatus !== "pending") {
      const currentStatusOption = juryOptions.find(
        (option) => option.value === row.applicationStatus
      );
      updateFormikJury.setValues({
        applicationStatus: currentStatusOption || "",
        remarks:
          row?.juryRemarks?.filter((item) => item?.addedBy?._id === user?._id)
            ?.remark || "",
      });
      setSelectedRole(currentStatusOption || null);
    } else {
      updateFormikJury.resetForm();
      setSelectedRole(null);
    }

    setJuryStatusDialog(true);
  };

  /* HANDLE STATUS DIALOG CLOSE */
  const handleStatusDialogClose = () => {
    setStatusDialog(false);
    updateFormik.resetForm();
    setSelectedRole(null);
    setSelectedId(null);
  };

  /* HANDLE STATUS DIALOG CLOSE */
  const handleStatusDialogCloseJury = () => {
    setJuryStatusDialog(false);
    updateFormikJury.resetForm();
    setSelectedRole(null);
    setSelectedId(null);
  };

  /* COLUMNS */
  const columns = [
    {
      name: "Sr. No",
      grow: 0.5,
      selector: (row, index) => (
        <span className="text-sm font-ibm-regular text-black text-opacity-60">
          {index + 1}
        </span>
      ),
    },
    {
      name: "Applicant Name",
      grow: 1.2,
      selector: (row) => (
        <div className="flex flex-col py-2">
          <p className="text-sm font-ibm-semibold text-black">
            {getFullName(row)}
          </p>
          <div className="mt-1">
            <a
              href={`mailto:${row.email}`}
              className="block text-sm text-blue-500 hover:underline"
            >
              {row.email}
            </a>
            <a
              href={`tel:${row.mobileNumber}`}
              className="mt-1 block text-sm text-blue-500 hover:underline"
            >
              {row.mobileNumber}
            </a>
          </div>
        </div>
      ),
    },
    {
      name: "Award Category",
      grow: 1.5,
      selector: (row) => (
        <p
          style={{ overflowWrap: "anywhere" }}
          className="text-sm font-ibm-semibold text-black"
        >
          {row.awardCategory}
        </p>
      ),
    },
    {
      name: "Status",
      grow: 0.8,
      selector: (row) => {
        return (
          <div className="flex items-center gap-2 flex-wrap">
            {row?.applicationStatus === "eligible" ? (
              <div className="bg-green-500/10 border border-green-500/50 text-green-600 text-sm font-ibm-regular px-3 py-1 rounded-md">
                Eligible
              </div>
            ) : row?.applicationStatus === "ineligible" ? (
              <div className="bg-orange-500/10 border border-orange-500/50 text-orange-600 text-sm font-ibm-regular px-3 py-1 rounded-md">
                Ineligible
              </div>
            ) : row?.applicationStatus === "rejected" ? (
              <div className="bg-red-500/10 border border-red-500/50 text-red-600 text-sm font-ibm-regular px-3 py-1 rounded-md">
                Rejected
              </div>
            ) : row?.applicationStatus === "pending" ? (
              <div className="bg-greyBg/50 border border-greyBorder text-greyText text-sm font-ibm-regular px-3 py-1 rounded-md">
                Pending
              </div>
            ) : row?.applicationStatus === "selected" ? (
              <div className="bg-green-500 border border-green-500 text-white text-sm font-ibm-regular px-3 py-1 rounded-md">
                Selected
              </div>
            ) : row?.applicationStatus === "not-selected" ? (
              <div className="bg-red-500 border border-red-500 text-white text-sm font-ibm-regular px-3 py-1 rounded-md">
                Not selected
              </div>
            ) : (
              <></>
            )}
            {row?.isShortListed && (
              <div className="bg-logoRed/10 border border-logoRed text-logoRed text-sm font-ibm-regular px-3 py-1 rounded-md">
                Shortlisted
              </div>
            )}
          </div>
        );
      },
    },
    {
      name: "Date applied",
      selector: (row) => (
        <React.Fragment>
          <p
            style={{ overflowWrap: "anywhere" }}
            className="text-sm font-ibm-regular text-greyText pr-5"
          >
            {formatDate(row.createdAt)}
          </p>
        </React.Fragment>
      ),
    },
    {
      name: "Actions",
      grow: 0.2,
      selector: (row) => (
        <Menu>
          <MenuHandler>
            <Button className="shadow-none hover:shadow-none p-1.5 bg-greyBg border border-greyBorder hover:border-black/40 rounded-full transition-all duration-300 ease-in-out">
              <GoKebabHorizontal className="w-4 h-4 text-greyText rotate-90" />
            </Button>
          </MenuHandler>
          <MenuList className="p-1.5 min-w-[20vh] bg-white">
            {userRole === "OFFICE-BEARER" && (
              <MenuItem
                onClick={() => handleStatusDialogOpen(row)}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                Update status
              </MenuItem>
            )}
            {userRole === "JURY" && (
              <MenuItem
                onClick={() => handleStatusDialogOpenJury(row)}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                Update status
              </MenuItem>
            )}
            {row?.officeBearerRemarks?.length > 0 && (
              <MenuItem
                onClick={() => {
                  setRewardsDialog(!rewardsDialog);
                  setSelectedUserDetails(row);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                View remarks
              </MenuItem>
            )}
            {userRole === "OFFICE-BEARER" && (
              <MenuItem
                onClick={() => {
                  setSelectedApplication(row);
                  setEditTab("Personal Information");
                  setEditModal(true);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                Edit application
              </MenuItem>
            )}
            <MenuItem
              onClick={() => viewApplicationDetails(row)}
              className="text-sm font-ibm-regular px-4 py-2 text-black"
            >
              View details
            </MenuItem>
            <MenuItem
              onClick={() => {
                setSelectedApplication(row);
                setDetailsModal(true);
                setTimeout(() => {
                  exportPDF(row);
                }, 500);
              }}
              className="text-sm font-ibm-regular px-4 py-2 text-black"
            >
              Export to PDF
            </MenuItem>
            {userRole === "OFFICE-BEARER" &&
            row?.applicationStatus === "selected" ? (
              <MenuItem
                onClick={() => {
                  setSendEmailDialog(!sendEmailDialog);
                  setDeleteId(row?._id);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                Send email to student
              </MenuItem>
            ) : (
              <></>
            )}
            {userRole === "OFFICE-BEARER" && (
              <MenuItem
                onClick={() => {
                  setDeleteId(row?._id);
                  setDeleteModal(!deleteModal);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-red-400"
              >
                Delete application
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      ),
    },
  ];

  /* USE-EFFECTS */
  useEffect(() => {
    if (userRole !== "" && defaultSelected !== null) {
      getDataListFunction();
    }
  }, [
    page,
    sizePerPage,
    debouncedSearch,
    selectedCategory,
    selectedState,
    selectedZone,
    defaultSelected,
    selectedGraduationProgram,
    selectedPostGraduationProgram,
    selectedBoard,
    selectedQualification,
    selectedCourseForMedical,
    selectedCourseForGoldMedalists,
  ]);

  /* USE-EFFECTS */
  useEffect(() => {
    const formattedOptions = [
      { value: null, label: "All states" },
      ...statesOfIndia.map((state) => ({
        value: state,
        label: state,
      })),
    ];

    setStateOptions(formattedOptions);
  }, []);

  /* USE-EFFECTS */
  useEffect(() => {
    const formattedOptions = [
      { value: null, label: "All zones" },
      ...zones.map((zone) => ({
        value: zone,
        label: zone,
      })),
    ];

    setZoneOptions(formattedOptions);
  }, []);

  /* LOADING SCREEN FOR DATATABLE */
  const LoadingScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-regular text-black text-center">
          Please wait!
        </p>
        <p className="text-sm font-ibm-regular text-black text-center">
          We are fetching data{" "}
        </p>
      </div>
    );
  };

  /* NO DATA SCREEN */
  const NoDataScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-regular text-black text-center">
          There are no students to display.
        </p>
      </div>
    );
  };

  /* RENDER DETAIL */
  const renderDetailField = (label, value, isLink = false) => {
    // Check if it's a file and determine if it's an image
    const isImage =
      isLink &&
      value &&
      /\.(jpg|jpeg|png|gif|webp)$/i.test(value.toLowerCase());

    // Get filename from path for download attribute
    const getFilenameFromPath = (path) => {
      if (!path) return "";
      // Extract filename from path
      const parts = path.split("/");
      return parts[parts.length - 1];
    };

    return (
      <div className="mb-5 group">
        <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
        {isLink && value ? (
          isImage && label !== "Signature" ? (
            <div className="w-fit mb-2 relative">
              <img
                src={getFullFileUrl(value)}
                alt={label}
                className="max-h-40 object-contain border border-gray-200 rounded-md shadow-sm hover:shadow-md transition-shadow duration-200 aspect-square bg-white"
              />
              <div className="absolute top-1 right-1">
                <div className="flex items-center gap-1">
                  <a
                    href={getFullFileUrl(value)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-red-600 hover:text-red-800 mt-1 border border-red-100 px-2 py-1 rounded-md bg-red-50 flex items-center gap-2 font-ibm-regular"
                  >
                    <FaDownload className="w-3 h-3 text-red-700" /> View
                  </a>
                </div>
              </div>
            </div>
          ) : isImage && label === "Signature" ? (
            <div className="w-fit mb-2 relative">
              <img
                src={getFullFileUrl(value)}
                alt="Signature"
                className="max-h-40 w-[30vh] object-contain rounded-md bg-white"
                style={{ pointerEvents: "none" }}
                onContextMenu={(e) => e.preventDefault()}
                draggable={false}
              />
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <a
                href={getFullFileUrl(value)}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-red-600 hover:text-red-800 hover:underline flex items-center gap-1 transition-colors duration-200 bg-red-50 px-4 py-2 rounded-md border border-red-100 font-ibm-regular"
              >
                <FaDownload size={12} className="text-red-700" /> View Document
              </a>
            </div>
          )
        ) : (
          <p className="text-sm bg-greyBg border border-greyBorder text-black px-3 py-2 rounded-md transition-all duration-200 font-ibm-regular">
            {value || "N/A"}
          </p>
        )}
      </div>
    );
  };

  /* RENDER EDITABLE FIELDS */
  const renderEditField = (label, name, type = "text") => (
    <div className="mb-0">
      <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
      <Input
        name={name}
        type={type}
        value={editFormik.values[name]}
        onChange={editFormik.handleChange}
        className="w-full"
      />
    </div>
  );

  /* RENDER TEXTAREA */
  const renderTextareaField = (label, name, rows = "3") => (
    <div className="mb-0">
      <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
      <textarea
        rows={rows}
        name={name}
        value={editFormik.values[name]}
        onChange={editFormik.handleChange}
        className="w-full block px-3 py-2 rounded-md border border-authBorder text-sm font-ibm-regular text-black placeholder:text-sm placeholder:text-gray-500 placeholder:font-ibm-regular focus:outline-none"
      />
    </div>
  );

  /* RENDER DROPDOWN */
  const renderDropdownField = (label, name, options, firstOption) => (
    <div className="mb-0">
      <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
      <div className="px-3 py-2 rounded-md border border-authBorder">
        <select
          name={name}
          value={editFormik.values[name]}
          onChange={editFormik.handleChange}
          className="w-full block text-sm font-ibm-regular text-black focus:outline-none"
        >
          <option value="">{firstOption}</option>
          {options.map((program) => (
            <option key={program.value} value={program.value}>
              {program.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  /* RENDER FILE FIELDS */
  const renderFileField = (label, name) => (
    <div className="mb-5">
      <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
      <input
        type="file"
        name={name}
        onChange={(e) => editFormik.setFieldValue(name, e.target.files[0])}
        className="w-full font-ibm-regular"
      />
      {editFormik.values[name] &&
        typeof editFormik.values[name] === "string" && (
          <a
            href={getFullFileUrl(editFormik.values[name])}
            target="_blank"
            rel="noreferrer"
            className="mt-2 text-sm text-blue-500 hover:underline block font-ibm-regular underline"
          >
            View current uploaded file
          </a>
        )}
    </div>
  );

  /* EDIT FORMIK */
  const editFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      /* PERSONAL INFORMATION */
      firstName: selectedApplication?.firstName || "",
      middleName: selectedApplication?.middleName || "",
      lastName: selectedApplication?.lastName || "",
      email: selectedApplication?.email || "",
      mobileNumber: selectedApplication?.mobileNumber || "",
      dateOfBirth:
        moment(selectedApplication?.dateOfBirth).format("YYYY-MM-DD") || "",
      lohanaConnectId: selectedApplication?.lohanaConnectId || "",
      dateOfApplication: moment(selectedApplication?.dateOfApplication).format(
        "YYYY-MM-DD"
      ),
      address: selectedApplication?.address || "",
      state: selectedApplication?.state || "",
      district: selectedApplication?.district || "",
      zone: selectedApplication?.zone || "",
      mahajan: selectedApplication?.mahajan || "",
      awardCategory: Array.isArray(selectedApplication?.awardCategory)
        ? selectedApplication.awardCategory.join(", ")
        : selectedApplication?.awardCategory || "",
      photograph: selectedApplication?.photograph || "",
      applicantSignature: selectedApplication?.applicantSignature || "",

      /* CLASS - 10th AND 12th */
      class_boardName: selectedApplication?.class?.boardName || "",
      class_obtainedMarks: selectedApplication?.class?.obtainedMarks || "",
      class_totalMarks: selectedApplication?.class?.totalMarks || "",
      class_achievements: selectedApplication?.class?.achievements || "",
      class_marksheet: selectedApplication?.class?.marksheet || "",

      /* GRADUATION */
      graduation_program: selectedApplication?.graduation?.program || "",
      graduation_course: selectedApplication?.graduation?.course || "",
      graduation_specialization:
        selectedApplication?.graduation?.specialization || "",
      graduation_university: selectedApplication?.graduation?.university || "",
      graduation_marks: selectedApplication?.graduation?.marks || "",
      graduation_totalMarks:
        selectedApplication?.graduation?.graduationTotalMarks || "",
      graduation_achievements:
        selectedApplication?.graduation?.achievements || "",
      graduation_marksheet: selectedApplication?.graduation?.marksheet || "",

      /* POST GRADUATION */
      postGraduation_program:
        selectedApplication?.postGraduation?.program || "",
      postGraduation_course: selectedApplication?.postGraduation?.course || "",
      postGraduation_specialization:
        selectedApplication?.postGraduation?.specialization || "",
      postGraduation_university:
        selectedApplication?.postGraduation?.university || "",
      postGraduation_marks: selectedApplication?.postGraduation?.marks || "",
      postGraduation_totalMarks:
        selectedApplication?.postGraduation?.graduationTotalMarks || "",
      postGraduation_achievements:
        selectedApplication?.postGraduation?.achievements || "",
      postGraduation_marksheet:
        selectedApplication?.postGraduation?.marksheet || "",

      /* PHD */
      phd_achievements: selectedApplication?.phd?.achievements || "",
      phd_degreeCertificate: selectedApplication?.phd?.degreeCertificate || "",
      phd_department: selectedApplication?.phd?.department || "",
      phd_thesisTitle: selectedApplication?.phd?.thesisTitle || "",
      phd_universityGazette: selectedApplication?.phd?.universityGazette || "",

      /* PROFESSIONAL */
      caCaIcwa_achievements: selectedApplication?.caCaIcwa?.achievements || "",
      caCaIcwa_finalExamMarks:
        selectedApplication?.caCaIcwa?.finalExamMarks || "",
      caCaIcwa_finalExamPassedMonth:
        selectedApplication?.caCaIcwa?.finalExamPassedMonth || "",
      caCaIcwa_finalExamPassedYear:
        selectedApplication?.caCaIcwa?.finalExamPassedYear || "",
      caCaIcwa_finalExamTotalMarks:
        selectedApplication?.caCaIcwa?.finalExamTotalMarks || "",
      caCaIcwa_finalMarksheet:
        selectedApplication?.caCaIcwa?.finalMarksheet || "",
      caCaIcwa_foundationExamMarks:
        selectedApplication?.caCaIcwa?.foundationExamMarks || "",
      caCaIcwa_foundationExamPassedMonth:
        selectedApplication?.caCaIcwa?.foundationExamPassedMonth || "",
      caCaIcwa_foundationExamPassedYear:
        selectedApplication?.caCaIcwa?.foundationExamPassedYear || "",
      caCaIcwa_foundationExamTotalMarks:
        selectedApplication?.caCaIcwa?.foundationExamTotalMarks || "",
      caCaIcwa_foundationMarksheet:
        selectedApplication?.caCaIcwa?.foundationMarksheet || "",
      caCaIcwa_institute: selectedApplication?.caCaIcwa?.institute || "",
      caCaIcwa_intermediateExamMarks:
        selectedApplication?.caCaIcwa?.intermediateExamMarks || "",
      caCaIcwa_intermediateExamPassedMonth:
        selectedApplication?.caCaIcwa?.intermediateExamPassedMonth || "",
      caCaIcwa_intermediateExamPassedYear:
        selectedApplication?.caCaIcwa?.intermediateExamPassedYear || "",
      caCaIcwa_intermediateExamTotalMarks:
        selectedApplication?.caCaIcwa?.intermediateExamTotalMarks || "",
      caCaIcwa_intermediateMarksheet:
        selectedApplication?.caCaIcwa?.intermediateMarksheet || "",
      caCaIcwa_qualification:
        selectedApplication?.caCaIcwa?.qualification || "",

      /* CIVIL SERVICES */
      civilServices_commission:
        selectedApplication?.civilServices?.commission || "",
      civilServices_institute:
        selectedApplication?.civilServices?.institute || "",
      civilServices_letterOfPosting:
        selectedApplication?.civilServices?.letterOfPosting || "",
      civilServices_marks: selectedApplication?.civilServices?.marks || "",
      civilServices_passedMonth:
        selectedApplication?.civilServices?.passedMonth || "",
      civilServices_passedYear:
        selectedApplication?.civilServices?.passedYear || "",
      civilServices_rank: selectedApplication?.civilServices?.rank || "",
      civilServices_result: selectedApplication?.civilServices?.result || "",
      civilServices_totalMarks:
        selectedApplication?.civilServices?.totalMarks || "",
      civilServices_achievements:
        selectedApplication?.civilServices?.achievements || "",

      affiliation_institution:
        selectedApplication?.affiliation?.institution || "",
      affiliation_department:
        selectedApplication?.affiliation?.department || "",
      affiliation_university:
        selectedApplication?.affiliation?.university || "",

      /* SUPPORTING DOCUMENTS */
      additionalDocuments:
        selectedApplication?.documents?.additionalDocuments || "",
      certifiedTranscriptsAndAchievementCertificates:
        selectedApplication?.documents
          ?.certifiedTranscriptsAndAchievementCertificates || "",
      proofOfIdentity: selectedApplication?.documents?.proofOfIdentity || "",
      letterOfRecommendation:
        selectedApplication?.documents?.letterOfRecommendation || "",
    },
    onSubmit: (values) => {
      setFormLoading(true);
      const formData = new FormData();

      // Add simple fields
      formData.append("firstName", values.firstName);
      formData.append("middleName", values.middleName);
      formData.append("lastName", values.lastName);
      formData.append("email", values.email);
      formData.append("mobileNumber", values.mobileNumber);
      formData.append("dateOfBirth", values.dateOfBirth);
      formData.append("dateOfApplication", values.dateOfApplication);
      formData.append("lohanaConnectId", values.lohanaConnectId);
      formData.append("address", values.address);
      formData.append("state", values.state);
      formData.append("district", values.district);
      formData.append("zone", values.zone);
      formData.append("mahajan", values.mahajan);
      formData.append("awardCategory", values.awardCategory);

      /* FILES OR STRINGS */
      if (values.photograph) formData.append("photograph", values.photograph);
      if (values.applicantSignature)
        formData.append("applicantSignature", values.applicantSignature);

      /* BOARD */
      if (
        values?.awardCategory === "Secondary School Scholars (Std 10)" ||
        values?.awardCategory === "Higher Secondary School Scholars (Class 12)"
      ) {
        formData.append("class.boardName", values.class_boardName);
        formData.append("class.obtainedMarks", values.class_obtainedMarks);
        formData.append("class.totalMarks", values.class_totalMarks);
        formData.append("class.achievements", values.class_achievements);
        formData.append("class.marksheet", values.class_marksheet);
      }

      /* GRADUATION */
      if (
        values?.awardCategory === "Bachelors Program Toppers" ||
        (values?.awardCategory === "University Gold Medalists" &&
          Object.keys(selectedApplication?.graduation).length > 0) ||
        (values?.awardCategory === "Medical Degree Achievers" &&
          Object.keys(selectedApplication?.graduation).length > 0)
      ) {
        formData.append("graduation.program", values.graduation_program);
        formData.append("graduation.course", values.graduation_course);
        formData.append(
          "graduation.specialization",
          values.graduation_specialization
        );
        formData.append("graduation.university", values.graduation_university);
        formData.append("graduation.marks", values.graduation_marks);
        formData.append(
          "graduation.graduationTotalMarks",
          values.graduation_totalMarks
        );
        formData.append(
          "graduation.achievements",
          values.graduation_achievements
        );
        formData.append("graduation.marksheet", values.graduation_marksheet);
      }

      /* POST GRADUATION */
      if (
        values?.awardCategory === "Masters Program Toppers" ||
        (values?.awardCategory === "University Gold Medalists" &&
          Object.keys(selectedApplication?.postGraduation).length > 0) ||
        (values?.awardCategory === "Medical Degree Achievers" &&
          Object.keys(selectedApplication?.postGraduation).length > 0)
      ) {
        formData.append(
          "postGraduation.program",
          values.postGraduation_program
        );
        formData.append("postGraduation.course", values.postGraduation_course);
        formData.append(
          "postGraduation.specialization",
          values.postGraduation_specialization
        );
        formData.append(
          "postGraduation.university",
          values.postGraduation_university
        );
        formData.append("postGraduation.marks", values.postGraduation_marks);
        formData.append(
          "postGraduation.graduationTotalMarks",
          values.postGraduation_totalMarks
        );
        formData.append(
          "postGraduation.achievements",
          values.postGraduation_achievements
        );
        formData.append(
          "postGraduation.marksheet",
          values.postGraduation_marksheet
        );
      }

      /* PHD */
      if (values?.awardCategory === "Ph.D Achievers") {
        formData.append("phd.achievements", values.phd_achievements);
        formData.append("phd.degreeCertificate", values.phd_degreeCertificate);
        formData.append("phd.department", values.phd_department);
        formData.append("phd.thesisTitle", values.phd_thesisTitle);
        formData.append("phd.universityGazette", values.phd_universityGazette);
      }

      /* PROFESSIONAL */
      if (
        values?.awardCategory === "Accountancy & Company Secretary Achievers"
      ) {
        formData.append("caCaIcwa.achievements", values.caCaIcwa_achievements);
        formData.append(
          "caCaIcwa.finalExamMarks",
          values.caCaIcwa_finalExamMarks
        );
        formData.append(
          "caCaIcwa.finalExamPassedMonth",
          values.caCaIcwa_finalExamPassedMonth
        );
        formData.append(
          "caCaIcwa.finalExamPassedYear",
          values.caCaIcwa_finalExamPassedYear
        );
        formData.append(
          "caCaIcwa.finalExamTotalMarks",
          values.caCaIcwa_finalExamTotalMarks
        );
        formData.append(
          "caCaIcwa.finalMarksheet",
          values.caCaIcwa_finalMarksheet
        );
        formData.append(
          "caCaIcwa.foundationExamMarks",
          values.caCaIcwa_foundationExamMarks
        );
        formData.append(
          "caCaIcwa.foundationExamPassedMonth",
          values.caCaIcwa_foundationExamPassedMonth
        );
        formData.append(
          "caCaIcwa.foundationExamPassedYear",
          values.caCaIcwa_foundationExamPassedYear
        );
        formData.append(
          "caCaIcwa.foundationExamTotalMarks",
          values.caCaIcwa_foundationExamTotalMarks
        );
        formData.append(
          "caCaIcwa.foundationMarksheet",
          values.caCaIcwa_foundationMarksheet
        );
        formData.append("caCaIcwa.institute", values.caCaIcwa_institute);
        formData.append(
          "caCaIcwa.intermediateExamMarks",
          values.caCaIcwa_intermediateExamMarks
        );
        formData.append(
          "caCaIcwa.intermediateExamPassedMonth",
          values.caCaIcwa_intermediateExamPassedMonth
        );
        formData.append(
          "caCaIcwa.intermediateExamPassedYear",
          values.caCaIcwa_intermediateExamPassedYear
        );
        formData.append(
          "caCaIcwa.intermediateExamTotalMarks",
          values.caCaIcwa_intermediateExamTotalMarks
        );
        formData.append(
          "caCaIcwa.intermediateMarksheet",
          values.caCaIcwa_intermediateMarksheet
        );
        formData.append(
          "caCaIcwa.qualification",
          values.caCaIcwa_qualification
        );
      }

      /* CIVIL SERVICES */
      if (values?.awardCategory === "Civil Services Achievers") {
        formData.append(
          "civilServices.commission",
          values.civilServices_commission
        );
        formData.append(
          "civilServices.institute",
          values.civilServices_institute
        );
        formData.append(
          "civilServices.letterOfPosting",
          values.civilServices_letterOfPosting
        );
        formData.append("civilServices.marks", values.civilServices_marks);
        formData.append(
          "civilServices.passedMonth",
          values.civilServices_passedMonth
        );
        formData.append(
          "civilServices.passedYear",
          values.civilServices_passedYear
        );
        formData.append("civilServices.rank", values.civilServices_rank);
        formData.append("civilServices.result", values.civilServices_result);
        formData.append(
          "civilServices.totalMarks",
          values.civilServices_totalMarks
        );
        formData.append(
          "civilServices.achievements",
          values.civilServices_achievements
        );
      }

      /* AFFILIATION */
      formData.append("affiliation.department", values.affiliation_department);
      formData.append(
        "affiliation.institution",
        values.affiliation_institution
      );
      formData.append("affiliation.university", values.affiliation_university);

      /* DOCUMENTS */
      const docFields = [
        "additionalDocuments",
        "certifiedTranscriptsAndAchievementCertificates",
        "proofOfIdentity",
        "letterOfRecommendation",
      ];
      docFields.forEach((field) => {
        if (values[field]) {
          formData.append(`documents.${field}`, values[field]);
        }
      });

      updateApplication(selectedApplication?._id, formData)
        .then((res) => {
          toast.success("Application updated successfully.");
        })
        .catch((err) => {
          console.log("Error: ", err);
        })
        .finally(() => {
          setFormLoading(false);
          setEditModal(false);
          setSelectedApplication(null);
          getDataListFunction();
        });
    },
  });

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      const userData = JSON.parse(user);
      setUserRole(userData.designation);
      if (userData.designation === "OFFICE-BEARER") {
        setDefaultSelected({ value: null, label: "All status" });
      } else if (userData.designation === "SUPER-ADMIN") {
        setDefaultSelected({ value: "eligible", label: "Eligible" });
      } else {
        setDefaultSelected({ value: null, label: "All status" });
      }
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  return (
    <section className="min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Student Applications | Admin Panel</title>
      </Helmet>

      <div className="container-full-width">
        {/* HEADING */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              Student Applications
            </h1>
            <h2 className="text-base font-ibm-regular text-greyText">
              Manage and view Student applications
            </h2>
          </div>
          <div className="w-full md:w-auto mt-2 md:mt-0 flex items-center gap-2">
            {/* EXCEL EXPORT BUTTON */}
            <ExcelExport
              data={exportData}
              fileName="Student Applications"
              btnText="Export data"
              onClickFn={async () => {
                try {
                  return await getExportList();
                } catch (error) {
                  console.error("Failed to prepare export data:", error);
                  throw error;
                }
              }}
            />

            {/* SEARCHBAR */}
            <div className="w-full md:w-[250px]">
              <Input
                type="text"
                placeholder="Search by student name, email."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* FILTERS */}
        <div className="mt-5 flex items-center flex-wrap gap-3 justify-end">
          {/* FILTER BY STATUS */}
          {userRole === "SUPER-ADMIN" ? (
            <div className="w-full md:w-[200px]">
              <Select
                options={statusOptionsForSuperAdmin}
                className="react-select"
                classNamePrefix="dropdown"
                value={defaultSelected}
                onChange={(e) => setDefaultSelected(e)}
                placeholder="Filter by state"
              />
            </div>
          ) : (
            <div className="w-full md:w-[200px]">
              <Select
                options={
                  userRole === "OFFICE-BEARER"
                    ? statusOptionsForOfficeBearer
                    : statusOptionsForJury
                }
                className="react-select"
                classNamePrefix="dropdown"
                value={defaultSelected}
                onChange={(e) => setDefaultSelected(e)}
                placeholder="Filter by state"
              />
            </div>
          )}

          {/* GRADUATION PROGRAM - BACHELOR PROGRAM TOPPERS */}
          {selectedCategory?.value === "Bachelors Program Toppers" && (
            <div className="w-full md:w-[250px]">
              <Select
                options={graduationPrograms}
                className="react-select"
                classNamePrefix="dropdown"
                value={selectedGraduationProgram}
                onChange={(e) => setSelectedGraduationProgram(e)}
                placeholder="Filter by graduation program"
              />
            </div>
          )}

          {/* GRADUATION PROGRAM - BACHELOR PROGRAM TOPPERS */}
          {selectedCategory?.value === "Masters Program Toppers" && (
            <div className="w-full md:w-[250px]">
              <Select
                options={graduationPrograms}
                className="react-select"
                classNamePrefix="dropdown"
                value={selectedPostGraduationProgram}
                onChange={(e) => setSelectedPostGraduationProgram(e)}
                placeholder="Filter by graduation program"
              />
            </div>
          )}

          {/* BOARD - 10th AND 12th OPTION */}
          {(selectedCategory?.value === "Secondary School Scholars (Std 10)" ||
            selectedCategory?.value ===
              "Higher Secondary School Scholars (Class 12)") && (
            <div className="w-full md:w-[200px]">
              <Select
                options={boardOptions}
                className="react-select"
                classNamePrefix="dropdown"
                value={selectedBoard}
                onChange={(e) => setSelectedBoard(e)}
                placeholder="Filter by board"
              />
            </div>
          )}

          {/* ACCOUNTANCY AND COMPANY SECRETARY ACHIEVERS */}
          {selectedCategory?.value ===
            "Accountancy & Company Secretary Achievers" && (
            <div className="w-full md:w-[250px]">
              <Select
                options={qualifications}
                className="react-select"
                classNamePrefix="dropdown"
                value={selectedQualification}
                onChange={(e) => setSelectedQualification(e)}
                placeholder="Filter by qualification"
              />
            </div>
          )}

          {/* MEDICAL DEGREE ACHIEVERS */}
          {selectedCategory?.value === "Medical Degree Achievers" && (
            <div className="w-full md:w-[250px]">
              <Select
                options={medicalGraduationCourse}
                className="react-select"
                classNamePrefix="dropdown"
                value={selectedCourseForMedical}
                onChange={(e) => setSelectedCourseForMedical(e)}
                placeholder="Filter by course"
              />
            </div>
          )}

          {/* SELECTED QUALIFICATION */}
          {selectedCategory?.value === "University Gold Medalists" && (
            <div className="w-full md:w-[250px]">
              <Select
                options={graduationPrograms}
                className="react-select"
                classNamePrefix="dropdown"
                value={selectedCourseForGoldMedalists}
                onChange={(e) => setSelectedCourseForGoldMedalists(e)}
                placeholder="Filter by course"
              />
            </div>
          )}

          {/* FILTER BY CATEGORY */}
          <div className="w-full md:w-[200px]">
            <Select
              options={options}
              className="react-select"
              classNamePrefix="dropdown"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e)}
              placeholder="Filter by award category"
            />
          </div>

          {/* FILTER BY ZONE */}
          <div className="w-full md:w-[200px]">
            <Select
              options={zoneOptions}
              className="react-select"
              classNamePrefix="dropdown"
              value={selectedZone}
              onChange={(e) => setSelectedZone(e)}
              placeholder={`${
                selectedZone === null ? "Select zone" : "Filter by zone"
              }`}
            />
          </div>

          {/* FILTER BY STATE */}
          <div className="w-full md:w-[200px]">
            <Select
              options={stateOptions}
              className="react-select"
              classNamePrefix="dropdown"
              value={selectedState}
              onChange={(e) => setSelectedState(e)}
              placeholder="Filter by state"
            />
          </div>

          {(selectedZone !== null ||
            selectedState !== null ||
            selectedGraduationProgram !== null ||
            selectedPostGraduationProgram !== null ||
            selectedQualification !== null ||
            selectedBoard !== null ||
            selectedCourseForMedical !== null ||
            selectedCourseForGoldMedalists !== null ||
            selectedCategory?.value !== null) && (
            <button
              onClick={() => {
                setSelectedZone(null);
                setSelectedState(null);
                setSelectedGraduationProgram(null);
                setSelectedPostGraduationProgram(null);
                setSelectedCategory({
                  value: null,
                  label: "All categories",
                });
                setSelectedBoard(null);
                setSelectedQualification(null);
                setSelectedCourseForMedical(null);
                setSelectedCourseForGoldMedalists(null);
              }}
              className="py-3 px-4 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200 font-ibm-regular text-sm"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* DATA TABLE */}
        <div className="mt-5">
          <Card className="bg-white border border-greyBorder rounded-lg">
            <CardBody className="p-5">
              <DataTable
                columns={columns}
                data={dataList}
                progressPending={loading}
                progressComponent={<LoadingScreen />}
                noDataComponent={<NoDataScreen />}
                pagination
                paginationServer
                paginationRowsPerPageOptions={[10, 15, 20, 25, 30, 50, 100]}
                paginationTotalRows={totalDocs}
                onChangeRowsPerPage={handlePerPageChange}
                onChangePage={handlePageChange}
                className="data-table"
              />
            </CardBody>
          </Card>
        </div>
      </div>

      {/* DELETE MODAL */}
      <Dialog
        open={deleteModal}
        handler={() => setDeleteModal(!deleteModal)}
        size="xs"
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder">
            <div className="flex items-center justify-between">
              <h5 className="text-base font-ibm-semibold text-black">
                Delete Application
              </h5>
              <Button
                onClick={() => setDeleteModal(!deleteModal)}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
              >
                <IoClose className="w-5 h-5 text-black" />
              </Button>
            </div>
          </div>

          {/* BODY */}
          <div className="p-5">
            <p className="text-sm font-ibm-regular text-black">
              Are you sure you want to delete this student application? This
              action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                onClick={() => setDeleteModal(!deleteModal)}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5 text-sm font-ibm-regular text-black"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white"
              >
                Delete
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>

      {/* APPLICATION DETAILS MODAL */}
      <Dialog
        open={detailsModal}
        handler={() => setDetailsModal(!detailsModal)}
        size="lg"
        className="bg-white rounded-xl shadow-xl"
      >
        <DialogBody className="p-0">
          {selectedApplication && (
            <>
              {/* HEADING */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-greyBorder rounded-t-xl">
                <h5 className="text-lg font-ibm-bold text-black">
                  Student Application Details
                </h5>
                <Button
                  onClick={() => {
                    setDetailsModal(!detailsModal);
                    setSelectedTab("Personal Information");
                  }}
                  className="p-2 rounded-full shadow-none hover:shadow-none bg-white hover:bg-red-50"
                >
                  <IoClose className="w-5 h-5 text-red-800" />
                </Button>
              </div>

              <div
                id="application-details-pdf"
                className="mt-0 max-h-[80vh] overflow-y-auto p-5"
              >
                {/* TABS */}
                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => setSelectedTab("Personal Information")}
                    className={`shadow-none hover:shadow-none ${
                      selectedTab === "Personal Information"
                        ? "text-white bg-logoRed font-ibm-semibold"
                        : "text-greyText bg-transparent font-ibm-regular"
                    } rounded-md normal-case font-normal text-sm py-2 px-5`}
                  >
                    Personal Information
                  </Button>

                  {(selectedApplication.class ||
                    selectedApplication.graduation ||
                    selectedApplication.postGraduation ||
                    selectedApplication.phd) && (
                    <Button
                      onClick={() => setSelectedTab("Education Details")}
                      className={`shadow-none hover:shadow-none ${
                        selectedTab === "Education Details"
                          ? "text-white bg-logoRed font-ibm-semibold"
                          : "text-greyText bg-transparent font-ibm-regular"
                      } rounded-md normal-case font-normal text-sm py-2 px-5`}
                    >
                      Education Details
                    </Button>
                  )}

                  {(selectedApplication.caCaIcwa ||
                    selectedApplication.civilServices) && (
                    <Button
                      onClick={() => setSelectedTab("Professional Details")}
                      className={`shadow-none hover:shadow-none ${
                        selectedTab === "Professional Details"
                          ? "text-white bg-logoRed font-ibm-semibold"
                          : "text-greyText bg-transparent font-ibm-regular"
                      } rounded-md normal-case font-normal text-sm py-2 px-5`}
                    >
                      Professional Details
                    </Button>
                  )}

                  <Button
                    onClick={() => setSelectedTab("Supporting Documents")}
                    className={`shadow-none hover:shadow-none ${
                      selectedTab === "Supporting Documents"
                        ? "text-white bg-logoRed font-ibm-semibold"
                        : "text-greyText bg-transparent font-ibm-regular"
                    } rounded-md normal-case font-normal text-sm py-2 px-5`}
                  >
                    Supporting Documents
                  </Button>
                </div>

                {/* PERSONAL INFORMATION TAB */}
                {selectedTab === "Personal Information" && (
                  <div className="mt-5 border border-greyBorder rounded-lg p-5 bg-white shadow">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div className="col-span-1">
                        {selectedApplication.firstName &&
                          renderDetailField(
                            "First Name",
                            selectedApplication?.firstName
                          )}
                      </div>
                      <div className="col-span-1">
                        {selectedApplication.middleName &&
                          renderDetailField(
                            "Middle Name",
                            selectedApplication?.middleName
                          )}
                      </div>
                      <div className="col-span-1">
                        {selectedApplication.lastName &&
                          renderDetailField(
                            "Last Name",
                            selectedApplication?.lastName
                          )}
                      </div>
                    </div>

                    {/* STATE, ZONE AND MAHAJAN */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                      <div className="col-span-1">
                        {selectedApplication.state &&
                          renderDetailField(
                            "State",
                            selectedApplication?.state
                          )}
                      </div>
                      <div className="col-span-1">
                        {selectedApplication.district &&
                          renderDetailField(
                            "District",
                            selectedApplication?.district
                          )}
                      </div>
                      <div className="col-span-1">
                        {selectedApplication.zone &&
                          renderDetailField("Zone", selectedApplication?.zone)}
                      </div>
                      <div className="col-span-1">
                        {selectedApplication.mahajan &&
                          renderDetailField(
                            "Mahajan",
                            selectedApplication?.mahajan
                          )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {selectedApplication.email && (
                        <div>
                          {selectedApplication.email &&
                            renderDetailField(
                              "Email",
                              selectedApplication.email
                            )}
                          {selectedApplication.mobileNumber &&
                            renderDetailField(
                              "Mobile",
                              selectedApplication.mobileNumber
                            )}
                          {selectedApplication.dateOfBirth &&
                            renderDetailField(
                              "Date of Birth",
                              formatDate(selectedApplication.dateOfBirth)
                            )}
                          {selectedApplication.address &&
                            renderDetailField(
                              "Address",
                              selectedApplication.address
                            )}
                          {selectedApplication?.state &&
                            renderDetailField(
                              "State",
                              selectedApplication?.state
                            )}
                          {selectedApplication?.district &&
                            renderDetailField(
                              "District",
                              selectedApplication?.district
                            )}
                          {selectedApplication?.zone &&
                            renderDetailField(
                              "Zone",
                              selectedApplication?.zone || "N/A"
                            )}
                        </div>
                      )}
                      <div>
                        {selectedApplication.dateOfApplication &&
                          renderDetailField(
                            "Application Date",
                            formatDate(selectedApplication.dateOfApplication)
                          )}
                        {selectedApplication.lohanaConnectId &&
                          renderDetailField(
                            "Lohana Connect ID",
                            selectedApplication.lohanaConnectId
                          )}
                        {selectedApplication.awardCategory &&
                          renderDetailField(
                            "Award Category",
                            Array.isArray(selectedApplication.awardCategory)
                              ? selectedApplication.awardCategory.join(", ")
                              : "N/A"
                          )}
                        {selectedApplication.photograph &&
                          renderDetailField(
                            "Photograph",
                            selectedApplication.photograph,
                            true
                          )}
                        {selectedApplication.applicantSignature &&
                          renderDetailField(
                            "Signature",
                            selectedApplication.applicantSignature,
                            true
                          )}
                      </div>
                    </div>
                  </div>
                )}

                {/* EDUCATION INFORMATION */}
                {selectedTab === "Education Details" &&
                  ((selectedApplication.class &&
                    selectedApplication.class.boardName) ||
                    selectedApplication.graduation ||
                    selectedApplication.postGraduation ||
                    selectedApplication.phd) && (
                    <div className="pt-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        {/* SCHOOL EDUCATION */}
                        {selectedApplication.class &&
                          selectedApplication.class.boardName && (
                            <div className="bg-white p-5 rounded-lg shadow-md border border-greyBorder">
                              <h4 className="font-ibm-bold text-base mb-3 text-logoRed">
                                School Education
                              </h4>
                              {renderDetailField(
                                "Board",
                                selectedApplication.class.boardName
                              )}
                              {renderDetailField(
                                "Marks",
                                `${selectedApplication.class.obtainedMarks}/${selectedApplication.class.totalMarks}`
                              )}
                              {renderDetailField(
                                "Achievements",
                                selectedApplication.class.achievements
                              )}
                              {renderDetailField(
                                "Marksheet",
                                selectedApplication.class.marksheet,
                                true
                              )}
                            </div>
                          )}

                        {/* GRADUATION */}
                        {selectedApplication.graduation && (
                          <div className="bg-white p-5 rounded-lg shadow-md border border-greyBorder">
                            <h4 className="font-ibm-bold text-sm mb-3 text-logoRed">
                              Graduation
                            </h4>
                            {renderDetailField(
                              "Program",
                              selectedApplication.graduation.program
                            )}
                            {renderDetailField(
                              "Course",
                              selectedApplication.graduation.course
                            )}
                            {renderDetailField(
                              "Specialization",
                              selectedApplication.graduation.specialization
                            )}
                            {renderDetailField(
                              "University",
                              selectedApplication.graduation.university
                            )}
                            {renderDetailField(
                              "Marks",
                              `${
                                selectedApplication.graduation.marks || "N/A"
                              }/${
                                selectedApplication.graduation
                                  .graduationTotalMarks || "N/A"
                              }`
                            )}
                            {renderDetailField(
                              "Achievements",
                              selectedApplication.graduation.achievements
                            )}
                            {renderDetailField(
                              "Marksheet",
                              selectedApplication.graduation.marksheet,
                              true
                            )}
                          </div>
                        )}

                        {/* POST GRADUATION */}
                        {selectedApplication.postGraduation && (
                          <div className="bg-white p-5 rounded-lg shadow-md border border-greyBorder">
                            <h4 className="font-ibm-bold text-base mb-3 text-logoRed">
                              Post Graduation
                            </h4>
                            {renderDetailField(
                              "Program",
                              selectedApplication.postGraduation.program
                            )}
                            {renderDetailField(
                              "Course",
                              selectedApplication.postGraduation.course
                            )}
                            {renderDetailField(
                              "Specialization",
                              selectedApplication.postGraduation.specialization
                            )}
                            {renderDetailField(
                              "University",
                              selectedApplication.postGraduation.university
                            )}
                            {renderDetailField(
                              "Marks",
                              `${
                                selectedApplication.postGraduation.marks ||
                                "N/A"
                              }/${
                                selectedApplication.postGraduation
                                  .graduationTotalMarks || "N/A"
                              }`
                            )}
                            {renderDetailField(
                              "Achievements",
                              selectedApplication.postGraduation.achievements
                            )}
                            {renderDetailField(
                              "Marksheet",
                              selectedApplication.postGraduation.marksheet,
                              true
                            )}
                          </div>
                        )}

                        {/* PhD */}
                        {selectedApplication.phd && (
                          <div className="bg-white p-5 rounded-lg shadow-md border border-greyBorder">
                            <h4 className="font-ibm-bold text-sm mb-3 text-logoRed">
                              PhD
                            </h4>
                            {renderDetailField(
                              "Department",
                              selectedApplication.phd.department
                            )}
                            {renderDetailField(
                              "University",
                              selectedApplication.phd.university
                            )}
                            {renderDetailField(
                              "Thesis Title",
                              selectedApplication.phd.thesisTitle
                            )}
                            {renderDetailField(
                              "Achievements",
                              selectedApplication.phd.achievements
                            )}
                            {renderDetailField(
                              "Degree Certificate",
                              selectedApplication.phd.degreeCertificate,
                              true
                            )}
                            {renderDetailField(
                              "University Gazette",
                              selectedApplication.phd.universityGazette,
                              true
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* PROFESSIONAL DETAILS */}
                {selectedTab === "Professional Details" &&
                  (selectedApplication.caCaIcwa ||
                    selectedApplication.civilServices) && (
                    <div className="mt-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        {/* CA/CA/ICWA */}
                        {selectedApplication.caCaIcwa && (
                          <div className="bg-white p-5 rounded-lg shadow-md border border-greyBorder">
                            <h4 className="font-ibm-bold text-base mb-3 text-red-700">
                              {selectedApplication.caCaIcwa.qualification}
                            </h4>
                            {renderDetailField(
                              "Institute",
                              selectedApplication.caCaIcwa.institute
                            )}
                            {renderDetailField(
                              "Foundation Exam",
                              `${selectedApplication.caCaIcwa.foundationExamMarks}/${selectedApplication.caCaIcwa.foundationExamTotalMarks} (${selectedApplication.caCaIcwa.foundationExamPassedMonth}/${selectedApplication.caCaIcwa.foundationExamPassedYear})`
                            )}
                            {renderDetailField(
                              "Foundation Marksheet",
                              selectedApplication.caCaIcwa.foundationMarksheet,
                              true
                            )}
                            {renderDetailField(
                              "Intermediate Exam",
                              `${selectedApplication.caCaIcwa.intermediateExamMarks}/${selectedApplication.caCaIcwa.intermediateExamTotalMarks} (${selectedApplication.caCaIcwa.intermediateExamPassedMonth}/${selectedApplication.caCaIcwa.intermediateExamPassedYear})`
                            )}
                            {renderDetailField(
                              "Intermediate Marksheet",
                              selectedApplication.caCaIcwa
                                .intermediateMarksheet,
                              true
                            )}
                            {renderDetailField(
                              "Final Exam",
                              `${selectedApplication.caCaIcwa.finalExamMarks}/${selectedApplication.caCaIcwa.finalExamTotalMarks} (${selectedApplication.caCaIcwa.finalExamPassedMonth}/${selectedApplication.caCaIcwa.finalExamPassedYear})`
                            )}
                            {renderDetailField(
                              "Final Marksheet",
                              selectedApplication.caCaIcwa.finalMarksheet,
                              true
                            )}
                            {renderDetailField(
                              "Achievements",
                              selectedApplication.caCaIcwa.achievements
                            )}
                          </div>
                        )}

                        {/* CIVIL SERVICES */}
                        {selectedApplication.civilServices && (
                          <div className="bg-white p-5 rounded-lg shadow-md border border-greyBorder">
                            <h4 className="font-ibm-bold text-base mb-3 text-red-700">
                              Civil Services
                            </h4>
                            {renderDetailField(
                              "Commission",
                              selectedApplication.civilServices.commission
                            )}
                            {renderDetailField(
                              "Institute",
                              selectedApplication.civilServices.institute
                            )}
                            {renderDetailField(
                              "Rank",
                              selectedApplication.civilServices.rank
                            )}
                            {renderDetailField(
                              "Marks",
                              `${selectedApplication.civilServices.marks}/${selectedApplication.civilServices.totalMarks}`
                            )}
                            {renderDetailField(
                              "Result",
                              selectedApplication.civilServices.result,
                              true
                            )}
                            {renderDetailField(
                              "Letter of Posting",
                              selectedApplication.civilServices.letterOfPosting,
                              true
                            )}
                          </div>
                        )}

                        {/* AFFILIATION DETAILS */}
                        {selectedApplication.affiliation && (
                          <div className="bg-white p-5 rounded-lg shadow-md border border-greyBorder">
                            {selectedApplication.affiliation
                              ? renderDetailField(
                                  "Institution",
                                  selectedApplication.affiliation.institution
                                )
                              : null}
                            {selectedApplication.affiliation
                              ? renderDetailField(
                                  "Department",
                                  selectedApplication.affiliation.department
                                )
                              : null}
                            {selectedApplication?.affiliation
                              ? renderDetailField(
                                  "University",
                                  selectedApplication.affiliation.university
                                )
                              : null}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* DOCUMENTS */}
                {selectedTab === "Supporting Documents" &&
                  selectedApplication.documents && (
                    <div className="mt-5 border border-greyBorder rounded-lg p-5 bg-white shadow">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {selectedApplication.documents &&
                        selectedApplication.documents.passportSizePhotograph
                          ? renderDetailField(
                              "Passport Size Photograph",
                              selectedApplication.documents
                                .passportSizePhotograph,
                              true
                            )
                          : null}
                        {selectedApplication.documents &&
                        selectedApplication.documents.proofOfIdentity
                          ? renderDetailField(
                              "Proof of Identity",
                              selectedApplication.documents.proofOfIdentity,
                              true
                            )
                          : null}
                        {selectedApplication.documents &&
                        selectedApplication.documents.letterOfRecommendation
                          ? renderDetailField(
                              "Letter of Recommendation",
                              selectedApplication.documents
                                .letterOfRecommendation,
                              true
                            )
                          : null}
                        {selectedApplication.documents &&
                        selectedApplication.documents
                          .copyOfRegistrationCertificate
                          ? renderDetailField(
                              "Copy of Registration Certificate",
                              selectedApplication.documents
                                .copyOfRegistrationCertificate,
                              true
                            )
                          : null}
                        {selectedApplication.documents &&
                        selectedApplication.documents
                          .certifiedTranscriptsAndAchievementCertificates
                          ? renderDetailField(
                              "Certified Transcripts and Achievement Certificates",
                              selectedApplication.documents
                                .certifiedTranscriptsAndAchievementCertificates,
                              true
                            )
                          : null}
                        {selectedApplication.documents &&
                        selectedApplication.documents.proofOfAge
                          ? renderDetailField(
                              "Proof of Age",
                              selectedApplication.documents.proofOfAge,
                              true
                            )
                          : null}
                        {selectedApplication.documents &&
                        selectedApplication.documents.registrationProof
                          ? renderDetailField(
                              "Registration Proof",
                              selectedApplication.documents.registrationProof,
                              true
                            )
                          : null}
                        {selectedApplication.documents &&
                        selectedApplication.documents.additionalDocuments
                          ? renderDetailField(
                              "Additional Documents",
                              selectedApplication.documents.additionalDocuments,
                              true
                            )
                          : null}
                      </div>
                    </div>
                  )}
              </div>
            </>
          )}
        </DialogBody>
      </Dialog>

      {/* STATUS DIALOG - OFFICE BEARER */}
      <Dialog open={statusDialog} handler={handleStatusDialogClose} size="xs">
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder flex items-center justify-between">
            <h5 className="text-base font-ibm-semibold text-black">
              Update status
            </h5>
            <Button
              onClick={handleStatusDialogClose}
              className="shadow-none hover:shadow-none bg-white hover:bg-black/10 rounded-full p-1 transition-all duration-300 ease-in-out"
            >
              <IoClose className="w-5 h-5 text-greyText" />
            </Button>
          </div>

          {/* BODY */}
          <form onSubmit={updateFormik.handleSubmit} className="p-5">
            {/* STATUS */}
            <div className="p-0">
              <p className="text-sm font-ibm-regular text-greyText">
                Please select the status of the application
              </p>
              <div className="mt-1">
                <Select
                  options={
                    isShortlistedValue === "eligible"
                      ? officeBearerOptionsUpdated
                      : officeBearerOptions
                  }
                  className="react-select"
                  value={updateFormik.values.applicationStatus}
                  onChange={(e) => {
                    updateFormik.setFieldValue("applicationStatus", e);
                    setSelectedRole(e);
                  }}
                  classNamePrefix="dropdown"
                  placeholder="Select role"
                />
              </div>
            </div>

            {/* REMARKS */}
            {selectedRole !== null && selectedRole?.label !== "Shortlisted" && (
              <div className="mt-5">
                <label className="text-sm font-ibm-regular text-greyText">
                  {selectedRole?.label === "Eligible" ||
                  selectedRole?.label === "Ineligible"
                    ? "Any remarks"
                    : "Please enter the reason of rejection"}
                </label>
                <div className="mt-1">
                  <textarea
                    rows={3}
                    placeholder={
                      selectedRole?.label === "Eligible" ||
                      selectedRole?.label === "Ineligible"
                        ? "Please enter the remarks"
                        : "Enter the reason of rejection"
                    }
                    name="remarks"
                    value={updateFormik.values.remarks}
                    onChange={updateFormik.handleChange}
                    className="block w-full px-5 py-3 rounded-md border border-authBorder  text-sm font-ibm-regular text-black placeholder:text-sm placeholder:text-gray-500 placeholder:font-ibm-regular focus:outline-none"
                  />
                </div>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                type="submit"
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white"
              >
                Update
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>

      {/* STATUS DIALOG - JURY */}
      <Dialog
        open={juryStatusDialog}
        handler={handleStatusDialogCloseJury}
        size="xs"
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder flex items-center justify-between">
            <h5 className="text-base font-ibm-semibold text-black">
              Update status
            </h5>
            <Button
              onClick={handleStatusDialogCloseJury}
              className="shadow-none hover:shadow-none bg-white hover:bg-black/10 rounded-full p-1 transition-all duration-300 ease-in-out"
            >
              <IoClose className="w-5 h-5 text-greyText" />
            </Button>
          </div>

          {/* BODY */}
          <form onSubmit={updateFormikJury.handleSubmit} className="p-5">
            {/* STATUS */}
            <div className="p-0">
              <p className="text-sm font-ibm-regular text-greyText">
                Please select the status of the application
              </p>
              <div className="mt-1">
                <Select
                  options={juryOptions}
                  className="react-select"
                  value={updateFormikJury.values.applicationStatus}
                  onChange={(e) => {
                    updateFormikJury.setFieldValue("applicationStatus", e);
                    setSelectedRole(e);
                  }}
                  classNamePrefix="dropdown"
                  placeholder="Select status"
                />
              </div>
            </div>

            {/* REMARKS */}
            {selectedRole !== null && (
              <div className="mt-5">
                <label className="text-sm font-ibm-regular text-greyText">
                  Remarks (if any)
                </label>
                <div className="mt-1">
                  <textarea
                    rows={3}
                    placeholder="Please enter the remarks"
                    name="remarks"
                    value={updateFormikJury.values.remarks}
                    onChange={updateFormikJury.handleChange}
                    className="block w-full px-5 py-3 rounded-md border border-authBorder  text-sm font-ibm-regular text-black placeholder:text-sm placeholder:text-gray-500 placeholder:font-ibm-regular focus:outline-none"
                  />
                </div>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                type="submit"
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white"
              >
                Update
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>

      {/* REMARKS DIALOG */}
      <Dialog
        open={rewardsDialog}
        handler={() => setRewardsDialog(!rewardsDialog)}
        size="md"
      >
        <DialogBody className="p-5 relative max-h-[80vh] overflow-auto hide-scrollbar">
          {/* OFFICE BEARER REMARKS */}
          <div className="pb-0">
            <h5 className="text-md font-ibm-semibold text-black">
              Office Bearer Remarks
            </h5>
            <div className="mt-2">
              {selectedUserDetails?.officeBearerRemarks?.map((item, index) => (
                <div
                  key={index}
                  className="mt-5 first:mt-0 bg-white border border-greyBorder rounded-lg shadow p-3"
                >
                  <div className="flex items-center justify-between pb-2 border-b border-greyBorder">
                    <div className="p-0">
                      <p className="text-black font-ibm-semibold text-base">
                        {item?.addedBy?.firstName} {item?.addedBy?.lastName}
                      </p>
                      <p className="text-sm font-ibm-regular text-greyText">
                        {moment(item?.createdAt).format("DD MMMM, HH:mm A")}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {item?.applicationStatus === "eligible" ? (
                        <div className="bg-green-500/10 border border-green-500/50 text-green-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Eligible
                        </div>
                      ) : item?.applicationStatus === "ineligible" ? (
                        <div className="bg-orange-500/10 border border-orange-500/50 text-orange-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Ineligible
                        </div>
                      ) : item?.applicationStatus === "rejected" ? (
                        <div className="bg-red-500/10 border border-red-500/50 text-red-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Rejected
                        </div>
                      ) : item?.applicationStatus === "pending" ? (
                        <div className="bg-greyBg/50 border border-greyBorder text-greyText text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Pending
                        </div>
                      ) : item?.applicationStatus === "selected" ? (
                        <div className="bg-green-500 border border-green-500 text-white text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Selected
                        </div>
                      ) : item?.applicationStatus === "not-selected" ? (
                        <div className="bg-red-500 border border-red-500 text-white text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Not selected
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>

                  <div className="pt-3">
                    <p className="text-black font-ibm-regular">
                      {item?.remark}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* JURY REMARKS */}
          {selectedUserDetails?.juryRemarks?.length > 0 && (
            <div className="pt-5">
              <h5 className="text-md font-ibm-semibold text-black">
                Jury Remarks
              </h5>
              <div className="mt-2">
                {selectedUserDetails?.juryRemarks?.map((item, index) => (
                  <div
                    key={index}
                    className="mt-5 first:mt-0 bg-white border border-greyBorder rounded-lg shadow p-3"
                  >
                    <div className="flex items-center justify-between pb-2 border-b border-greyBorder">
                      <div className="p-0">
                        <p className="text-black font-ibm-semibold text-base">
                          {item?.addedBy?.firstName} {item?.addedBy?.lastName}
                        </p>
                        <p className="text-sm font-ibm-regular text-greyText">
                          {moment(item?.createdAt).format("DD MMMM, HH:mm A")}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {item?.applicationStatus === "eligible" ? (
                          <div className="bg-green-500/10 border border-green-500/50 text-green-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Eligible
                          </div>
                        ) : item?.applicationStatus === "ineligible" ? (
                          <div className="bg-orange-500/10 border border-orange-500/50 text-orange-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Ineligible
                          </div>
                        ) : item?.applicationStatus === "rejected" ? (
                          <div className="bg-red-500/10 border border-red-500/50 text-red-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Rejected
                          </div>
                        ) : item?.applicationStatus === "pending" ? (
                          <div className="bg-greyBg/50 border border-greyBorder text-greyText text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Pending
                          </div>
                        ) : item?.applicationStatus === "selected" ? (
                          <div className="bg-green-500 border border-green-500 text-white text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Selected
                          </div>
                        ) : item?.applicationStatus === "not-selected" ? (
                          <div className="bg-red-500 border border-red-500 text-white text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Not selected
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>

                    <div className="pt-3">
                      <p className="text-black font-ibm-regular">
                        {item?.remark}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="absolute top-3 right-3">
            <Button
              onClick={() => setRewardsDialog(!rewardsDialog)}
              className="shadow-none hover:shadow-none bg-white hover:bg-black/10 rounded-full p-1 transition-all duration-300 ease-in-out"
            >
              <IoClose className="w-5 h-5 text-greyText" />
            </Button>
          </div>
        </DialogBody>
      </Dialog>

      {/* DELETE MODAL */}
      <Dialog
        open={sendEmailDialog}
        handler={() => setSendEmailDialog(!sendEmailDialog)}
        size="xs"
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder">
            <div className="flex items-center justify-between">
              <h5 className="text-base font-ibm-semibold text-black">
                Confirmation
              </h5>
              <Button
                onClick={() => setSendEmailDialog(!sendEmailDialog)}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
              >
                <IoClose className="w-5 h-5 text-black" />
              </Button>
            </div>
          </div>

          {/* BODY */}
          <div className="p-5">
            <p className="text-sm font-ibm-regular text-black">
              Are you sure you want to send email to this applicant? This action
              cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                onClick={() => setSendEmailDialog(!sendEmailDialog)}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5 text-sm font-ibm-regular text-black"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendEmail}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white"
              >
                Confirm
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>

      {/* EDIT MODAL */}
      <Dialog
        open={editModal}
        handler={() => setEditModal(!editModal)}
        size="lg"
        className="bg-white rounded-xl shadow-xl"
      >
        <DialogBody className="p-0">
          <form onSubmit={editFormik.handleSubmit}>
            {/* HEADER */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-greyBorder rounded-t-xl">
              <h5 className="text-lg font-ibm-bold text-black">
                Edit Student Application
              </h5>
              <Button
                onClick={() => setEditModal(false)}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-white hover:bg-red-50"
              >
                <IoClose className="w-5 h-5 text-red-800" />
              </Button>
            </div>

            {/* TABS */}
            <div className="flex items-center gap-3 p-5 pb-0">
              <Button
                onClick={() => setEditTab("Personal Information")}
                className={`shadow-none hover:shadow-none ${
                  editTab === "Personal Information"
                    ? "text-white bg-logoRed font-ibm-semibold"
                    : "text-greyText bg-transparent font-ibm-regular"
                } rounded-md normal-case font-normal text-sm py-2 px-5`}
              >
                Personal Information
              </Button>
              {(selectedApplication?.awardCategory[0] ===
                "Bachelors Program Toppers" ||
                selectedApplication?.awardCategory[0] ===
                  "Masters Program Toppers" ||
                selectedApplication?.awardCategory[0] === "Ph.D Achievers" ||
                selectedApplication?.awardCategory[0] ===
                  "University Gold Medalists" ||
                selectedApplication?.awardCategory[0] ===
                  "Secondary School Excellence (Std 10)" ||
                selectedApplication?.awardCategory[0] ===
                  "Higher Secondary School Scholars (Class 12)" ||
                selectedApplication?.awardCategory[0] ===
                  "Medical Degree Achievers") && (
                <Button
                  onClick={() => setEditTab("Education Details")}
                  className={`shadow-none hover:shadow-none ${
                    editTab === "Education Details"
                      ? "text-white bg-logoRed font-ibm-semibold"
                      : "text-greyText bg-transparent font-ibm-regular"
                  } rounded-md normal-case font-normal text-sm py-2 px-5`}
                >
                  Education Details
                </Button>
              )}

              {(selectedApplication?.awardCategory[0] ===
                "Accountancy & Company Secretary Achievers" ||
                selectedApplication?.awardCategory[0] ===
                  "Civil Services Achievers") && (
                <Button
                  onClick={() => setEditTab("Professional Details")}
                  className={`shadow-none hover:shadow-none ${
                    editTab === "Professional Details"
                      ? "text-white bg-logoRed font-ibm-semibold"
                      : "text-greyText bg-transparent font-ibm-regular"
                  } rounded-md normal-case font-normal text-sm py-2 px-5`}
                >
                  Professional Details
                </Button>
              )}

              <Button
                onClick={() => setEditTab("Supporting Documents")}
                className={`shadow-none hover:shadow-none ${
                  editTab === "Supporting Documents"
                    ? "text-white bg-logoRed font-ibm-semibold"
                    : "text-greyText bg-transparent font-ibm-regular"
                } rounded-md normal-case font-normal text-sm py-2 px-5`}
              >
                Supporting Documents
              </Button>
            </div>

            {/* CONTENT */}
            <div className="p-5 max-h-[65vh] overflow-y-auto">
              {/* PERSONAL INFO */}
              {editTab === "Personal Information" && (
                <div className="bg-white border border-greyBorder rounded-lg p-5 shadow">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {renderEditField("First Name", "firstName")}
                    {renderEditField("Middle Name", "middleName")}
                    {renderEditField("Last Name", "lastName")}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mt-5">
                    {renderEditField("State", "state")}
                    {renderEditField("District", "district")}
                    {renderEditField("Zone", "zone")}
                    {renderEditField("Mahajan", "mahajan")}
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-5">
                    {renderEditField("Email", "email")}
                    {renderEditField(
                      "Application date",
                      "dateOfApplication",
                      "date"
                    )}
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-5">
                    {renderEditField("Mobile Number", "mobileNumber")}
                    {renderEditField("Lohana Connect ID", "lohanaConnectId")}
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-5">
                    {renderEditField("Date of Birth", "dateOfBirth", "date")}
                    {renderEditField("Award Category", "awardCategory")}
                  </div>
                  <div className="mt-5">
                    {renderEditField("Address", "address")}
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-5">
                    {renderFileField("Photograph", "photograph")}
                    {renderFileField("Signature", "applicantSignature")}
                  </div>
                </div>
              )}

              {/* EDUCATION */}
              {editTab === "Education Details" && (
                <div className="bg-white border border-greyBorder rounded-lg p-5 shadow">
                  {selectedApplication?.class &&
                    Object.keys(selectedApplication?.class).length > 0 && (
                      <>
                        <h4 className="font-ibm-bold text-logoRed text-lg">
                          School / Class
                        </h4>
                        <div className="mt-5">
                          <div className="grid grid-cols-3 gap-5">
                            {renderDropdownField(
                              "Board Name",
                              "class_boardName",
                              boardOptions,
                              "Select board"
                            )}
                            {renderEditField(
                              "Marks Obtained",
                              "class_obtainedMarks"
                            )}
                            {renderEditField("Total Marks", "class_totalMarks")}
                          </div>
                          {selectedApplication?.class?.achievements && (
                            <div className="mt-5">
                              {renderEditField(
                                "Achievements",
                                "class_achievements"
                              )}
                            </div>
                          )}
                          <div className="mt-5">
                            {renderFileField("Marksheet", "class_marksheet")}
                          </div>
                        </div>
                      </>
                    )}

                  {selectedApplication?.graduation &&
                    Object.keys(selectedApplication?.graduation).length > 0 && (
                      <>
                        <h4 className="font-ibm-bold text-logoRed text-lg">
                          Graduation
                        </h4>
                        <div className="mt-3 grid grid-cols-3 gap-5">
                          {renderDropdownField(
                            "Program",
                            "graduation_program",
                            graduationPrograms,
                            "Select program"
                          )}
                          {renderEditField("Course", "graduation_course")}
                          {renderEditField(
                            "Specialization",
                            "graduation_specialization"
                          )}
                        </div>
                        <div className="mt-5 grid grid-cols-2 gap-5">
                          {renderEditField(
                            "University",
                            "graduation_university"
                          )}
                          {renderEditField(
                            `Marks / ${selectedApplication?.graduation?.graduationTotalMarks}`,
                            "graduation_marks"
                          )}
                        </div>
                        <div className="mt-5 grid grid-cols-3 gap-5">
                          <div className="col-span-2">
                            {renderTextareaField(
                              "Achievements",
                              "graduation_achievements"
                            )}
                          </div>
                          {renderFileField("Marksheet", "graduation_marksheet")}
                        </div>
                      </>
                    )}

                  {selectedApplication?.postGraduation &&
                    Object.keys(selectedApplication?.postGraduation).length >
                      0 && (
                      <>
                        <h4 className="font-ibm-bold text-logoRed text-lg">
                          Post Graduation
                        </h4>
                        <div className="mt-3 grid grid-cols-3 gap-5">
                          {renderDropdownField(
                            "Program",
                            "postGraduation_program",
                            graduationPrograms,
                            "Select program"
                          )}
                          {renderEditField("Course", "postGraduation_course")}
                          {renderEditField(
                            "Specialization",
                            "postGraduation_specialization"
                          )}
                          {renderEditField(
                            "University",
                            "postGraduation_university"
                          )}
                          {renderEditField("Marks", "postGraduation_marks")}
                          {renderEditField(
                            "Total Marks",
                            "postGraduation_totalMarks"
                          )}
                          {renderEditField(
                            "Achievements",
                            "postGraduation_achievements"
                          )}
                          {renderFileField(
                            "Marksheet",
                            "postGraduation_marksheet"
                          )}
                        </div>
                      </>
                    )}

                  {selectedApplication?.phd &&
                    Object.keys(selectedApplication?.phd).length > 0 && (
                      <>
                        <h4 className="font-ibm-bold text-logoRed text-lg">
                          Ph.D
                        </h4>
                        <div className="mt-3 grid grid-cols-2 gap-5">
                          {renderEditField("Thesis title", "phd_thesisTitle")}
                          {renderEditField("Department", "phd_department")}
                        </div>
                        <div className="mt-3">
                          {renderTextareaField(
                            "Achievements",
                            "phd_achievements"
                          )}
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-5">
                          {renderFileField(
                            "Degree Certificate",
                            "phd_degreeCertificate"
                          )}
                          {renderFileField(
                            "University Gazette",
                            "phd_universityGazette"
                          )}
                        </div>
                      </>
                    )}
                </div>
              )}

              {/* PROFESSIONAL */}
              {editTab === "Professional Details" && (
                <div className="bg-white border border-greyBorder rounded-lg p-5 shadow">
                  {selectedApplication?.caCaIcwa &&
                    Object.keys(selectedApplication?.caCaIcwa).length > 0 && (
                      <>
                        <h4 className="font-ibm-bold text-logoRed text-lg">
                          CA/CS/ICWA
                        </h4>
                        <div className="pb-5 border-b border-greyBorder">
                          <div className="mt-3 grid grid-cols-3 gap-5">
                            <div className="col-span-2">
                              {renderEditField(
                                "Name of institute",
                                "caCaIcwa_institute"
                              )}
                            </div>
                            <div className="col-span-1">
                              {renderDropdownField(
                                "Qualification",
                                "caCaIcwa_qualification",
                                qualifications,
                                "Select qualification"
                              )}
                            </div>
                          </div>
                          <div className="mt-5 grid grid-cols-4 gap-5">
                            {renderEditField(
                              "Foundation (Passed month)",
                              "caCaIcwa_foundationExamPassedMonth"
                            )}
                            {renderEditField(
                              "Foundation (Passed year)",
                              "caCaIcwa_foundationExamPassedYear"
                            )}
                            {renderEditField(
                              "Foundation (Obtained marks)",
                              "caCaIcwa_foundationExamMarks"
                            )}
                            {renderEditField(
                              "Foundation (Total marks)",
                              "caCaIcwa_foundationExamTotalMarks"
                            )}
                          </div>
                        </div>
                        <div className="pb-5 border-b border-greyBorder">
                          <div className="mt-5 grid grid-cols-4 gap-5">
                            {renderEditField(
                              "Intermediate (Passed month)",
                              "caCaIcwa_intermediateExamPassedMonth"
                            )}
                            {renderEditField(
                              "Intermediate (Passed year)",
                              "caCaIcwa_intermediateExamPassedYear"
                            )}
                            {renderEditField(
                              "Intermediate (Obtained marks)",
                              "caCaIcwa_intermediateExamMarks"
                            )}
                            {renderEditField(
                              "Intermediate (Total marks)",
                              "caCaIcwa_intermediateExamTotalMarks"
                            )}
                          </div>
                        </div>
                        <div className="pb-5">
                          <div className="mt-5 grid grid-cols-4 gap-5">
                            {renderEditField(
                              "Final (Passed month)",
                              "caCaIcwa_finalExamPassedMonth"
                            )}
                            {renderEditField(
                              "Final (Passed year)",
                              "caCaIcwa_finalExamPassedYear"
                            )}
                            {renderEditField(
                              "Final (Obtained marks)",
                              "caCaIcwa_finalExamMarks"
                            )}
                            {renderEditField(
                              "Final (Total marks)",
                              "caCaIcwa_finalExamTotalMarks"
                            )}
                          </div>
                        </div>
                        {selectedApplication?.caCaIcwa?.achievements && (
                          <div className="pb-5">
                            {renderTextareaField(
                              "Remarks/Description of Achievements",
                              "caCaIcwa_achievements"
                            )}
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-5">
                          {renderFileField(
                            "Foundation Marksheet",
                            "caCaIcwa_foundationMarksheet"
                          )}
                          {renderFileField(
                            "Intermediate Marksheet",
                            "caCaIcwa_intermediateMarksheet"
                          )}
                          {renderFileField(
                            "Final Marksheet",
                            "caCaIcwa_finalMarksheet"
                          )}
                        </div>
                      </>
                    )}

                  {selectedApplication?.civilServices &&
                    Object.keys(selectedApplication?.civilServices).length >
                      0 && (
                      <>
                        <h4 className="font-ibm-bold text-logoRed text-lg">
                          Civil Services
                        </h4>
                        <div className="mt-3 grid grid-cols-3 gap-5">
                          {renderEditField(
                            "Name of institute",
                            "civilServices_institute"
                          )}
                          {renderEditField(
                            "Commission",
                            "civilServices_commission"
                          )}
                          {renderEditField(
                            "National/State Rank",
                            "civilServices_rank"
                          )}
                        </div>
                        <div className="mt-5 grid grid-cols-4 gap-5">
                          {renderEditField(
                            `Passed month (${moment()
                              .month(
                                selectedApplication?.civilServices
                                  ?.passedMonth - 1
                              )
                              .format("MMMM")})`,
                            "civilServices_passedMonth"
                          )}
                          {renderEditField(
                            "Passed year",
                            "civilServices_passedYear"
                          )}
                          {renderEditField(
                            "Obtained marks",
                            "civilServices_marks"
                          )}
                          {renderEditField(
                            "Total marks",
                            "civilServices_totalMarks"
                          )}
                        </div>
                        {selectedApplication?.civilServices?.achievements && (
                          <div className="mt-5 grid grid-cols-3 gap-5">
                            {renderTextareaField(
                              "Remarks/Description of Achievements",
                              "civilServices_achievements"
                            )}
                          </div>
                        )}
                        <div className="mt-5 grid grid-cols-4 gap-5">
                          {renderFileField("Result", "civilServices_result")}
                          {renderFileField(
                            "Letter of posting",
                            "civilServices_letterOfPosting"
                          )}
                        </div>
                      </>
                    )}
                </div>
              )}

              {/* SUPPORTING DOCUMENTS */}
              {editTab === "Supporting Documents" && (
                <div className="bg-white border border-greyBorder rounded-lg p-5 shadow">
                  <div className="grid grid-cols-2 gap-5">
                    {renderFileField(
                      "Proof of Identity (Aadhar Card / PAN Card / Passport",
                      "proofOfIdentity"
                    )}
                    {renderFileField(
                      "Certified Transcripts and Achievement Certificates",
                      "certifiedTranscriptsAndAchievementCertificates"
                    )}
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-5">
                    {renderFileField(
                      "Letter of Recommendation",
                      "letterOfRecommendation"
                    )}
                    {renderFileField(
                      "Additional Supporting Documents",
                      "additionalDocuments"
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* FOOTER */}
            <div className="flex justify-end gap-2 p-5 border-t border-greyBorder">
              <Button
                onClick={() => setEditModal(false)}
                className="bg-greyBg text-black shadow-none hover:shadow-none font-ibm-semibold text-base"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-logoRed text-white shadow-none hover:shadow-none font-ibm-semibold text-base"
              >
                {formLoading ? "Updating..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>
    </section>
  );
};

export default Student;
